<template>
  <NotFoundView
    v-if="showNotFound"
    heading="Show not found"
    message="The show you were looking for could not be found."
  />
  <main v-else class="single-show-prototype">
    <section class="hero g-0 position-relative">
      <HeroTopPanel
        :logo-src="assets.logo"
        :background-src="backgroundUrl"
        :mobile-background-src="mobileBackgroundUrl"
        :background-wrap-style="heroBackgroundStyle"
        :login-path="loginPath"
        @open-mobile-search="openMobileSearchOverlay"
      >
        <div class="row g-0 hero__grid align-items-end">
          <div class="col-auto hero__posterWrap">
            <div
              :class="showPosterFrameClasses"
              data-testid="show-poster-frame"
            >
              <div
                v-if="showPosterLoading"
                class="image-load-frame__skeleton"
                data-testid="show-poster-skeleton"
                aria-hidden="true"
              ></div>
              <img
                v-if="showPosterImageSrc"
                :class="showPosterImageClasses"
                :src="showPosterImageSrc"
                :alt="`${showTitle} poster`"
                loading="eager"
                decoding="async"
                fetchpriority="high"
                @load="handleShowPosterLoad"
                @error="handleShowPosterError"
              >
            </div>
          </div>
          <div class="col hero__meta">
            <div class="upper-container">
              <div v-if="hasLoadedShowPayload" class="user-event-container">
                <h1 :class="['item-title', titleLengthClass]" data-testid="show-title">
                  {{ showTitle }}
                  <span class="release-year font-darker-gray weight300">{{ showYearDisplay }}</span>
                </h1>
                <div
                  v-if="showYearDisplay"
                  class="hero-release-year-tablet release-year font-darker-gray weight300"
                  data-testid="show-hero-release-year-tablet"
                >
                  {{ showYearDisplay }}
                </div>
                <div class="rating">
                  <div class="rate-box">
                    <div class="rate-text">
                      <p>
                        <span>{{ ratingLabelPrefix }}</span>
                        <span>{{ ratingLabelValue }}</span>
                        <button
                          v-if="isShowRated"
                          class="rating-reset"
                          data-testid="rating-reset-action"
                          type="button"
                          :disabled="showRatingPending"
                          aria-label="Remove rating"
                          @click="removeShowRating"
                        >
                          <svg
                            class="rating-reset__icon"
                            viewBox="0 0 16 16"
                            aria-hidden="true"
                            focusable="false"
                          >
                            <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.2" />
                            <path d="M5.2 5.2 10.8 10.8" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
                            <path d="M10.8 5.2 5.2 10.8" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
                          </svg>
                        </button>
                      </p>
                    </div>
                  </div>
                  <div class="rating-stars" @mouseleave="resetRatingPreview">
                    <template v-if="isAuthenticated">
                      <button
                        v-for="star in 10"
                        :key="`desktop-${star}`"
                        class="rating-star-button"
                        data-testid="rating-star"
                        type="button"
                        :disabled="showRatingPending"
                        :aria-label="`Rate ${star} out of 10`"
                        @mouseenter="previewRating(star)"
                        @focus="previewRating(star)"
                        @blur="resetRatingPreview"
                        @click="setShowRating(star)"
                      >
                        <i :class="ratingStarClass(star)"></i>
                      </button>
                    </template>
                    <template v-else>
                      <a
                        v-for="star in 10"
                        :key="`desktop-${star}`"
                        :href="loginPath"
                        data-testid="rating-star"
                        :aria-label="`Rate ${star} out of 10`"
                        @mouseenter="previewRating(star)"
                        @focus="previewRating(star)"
                        @blur="resetRatingPreview"
                      >
                        <i :class="ratingStarClass(star)"></i>
                      </a>
                    </template>
                  </div>
                </div>
                <div class="buttons buttons--without-calendar">
                  <template v-if="isAuthenticated">
                    <button
                      class="fs-btn left-btn"
                      :class="{ 'btn--watchlisted': isShowWatchlisted }"
                      data-testid="watchlist-action"
                      type="button"
                      :disabled="showWatchlistPending"
                      @click="toggleShowWatchlist"
                    >
                      <div class="default-btn add-to-watchlist">
                        <i class="ion-bookmark"></i> {{ watchlistButtonText }}
                      </div>
                      <div class="default-btn watchlist">
                        <i class="ion-bookmark"></i> {{ watchlistButtonShortText }}
                      </div>
                    </button>
                  </template>
                  <template v-else>
                    <a class="fs-btn left-btn" data-testid="watchlist-action" :href="loginPath">
                      <div class="default-btn add-to-watchlist">
                        <i class="ion-bookmark"></i> Add to watchlist
                      </div>
                      <div class="default-btn watchlist">
                        <i class="ion-bookmark"></i> Watchlist
                      </div>
                    </a>
                  </template>
                  <template v-if="isAuthenticated">
                    <button
                      class="fs-btn"
                      :class="[
                        { 'btn--favourited': isShowFavourited },
                        'right-btn',
                      ]"
                      data-testid="favourite-action"
                      type="button"
                      :disabled="showFavouritePending"
                      @click="toggleShowFavourite"
                    >
                      <div class="default-btn fav-btn">
                        <i class="ion-heart"></i> {{ favouriteButtonText }}
                      </div>
                      <div class="default-btn fav-btn-sm">
                        <i class="ion-heart"></i> {{ favouriteButtonShortText }}
                      </div>
                    </button>
                  </template>
                  <template v-else>
                    <a
                      class="fs-btn right-btn"
                      data-testid="favourite-action"
                      :href="loginPath"
                    >
                      <div class="default-btn fav-btn">
                        <i class="ion-heart"></i> Add to favourites
                      </div>
                      <div class="default-btn fav-btn-sm">
                        <i class="ion-heart"></i> Favourite
                      </div>
                    </a>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </HeroTopPanel>
    </section>

    <div v-if="hasLoadedShowPayload" class="container g-0 hero-tablet-controls">
      <div class="row g-0 hero-tablet-controls__row">
        <div class="col g-0">
          <div class="rating">
            <div class="rate-box">
              <div class="rate-text">
                <p>
                  <span>{{ ratingLabelPrefix }}</span>
                  <span>{{ ratingLabelValue }}</span>
                  <button
                    v-if="isShowRated"
                    class="rating-reset"
                    data-testid="rating-reset-action"
                    type="button"
                    :disabled="showRatingPending"
                    aria-label="Remove rating"
                    @click="removeShowRating"
                  >
                    <svg
                      class="rating-reset__icon"
                      viewBox="0 0 16 16"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.2" />
                      <path d="M5.2 5.2 10.8 10.8" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
                      <path d="M10.8 5.2 5.2 10.8" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
                    </svg>
                  </button>
                </p>
              </div>
            </div>
            <div class="rating-stars" @mouseleave="resetRatingPreview">
              <template v-if="isAuthenticated">
                <button
                  v-for="star in 10"
                  :key="`tablet-${star}`"
                  class="rating-star-button"
                  data-testid="rating-star"
                  type="button"
                  :disabled="showRatingPending"
                  :aria-label="`Rate ${star} out of 10`"
                  @mouseenter="previewRating(star)"
                  @focus="previewRating(star)"
                  @blur="resetRatingPreview"
                  @click="setShowRating(star)"
                >
                  <i :class="ratingStarClass(star)"></i>
                </button>
              </template>
              <template v-else>
                <a
                  v-for="star in 10"
                  :key="`tablet-${star}`"
                  :href="loginPath"
                  data-testid="rating-star"
                  :aria-label="`Rate ${star} out of 10`"
                  @mouseenter="previewRating(star)"
                  @focus="previewRating(star)"
                  @blur="resetRatingPreview"
                >
                  <i :class="ratingStarClass(star)"></i>
                </a>
              </template>
            </div>
          </div>
        </div>
      </div>
      <div class="row g-0 hero-tablet-controls__row">
        <div class="col g-0">
          <div class="buttons buttons--without-calendar">
            <template v-if="isAuthenticated">
              <button
                class="fs-btn left-btn"
                :class="{ 'btn--watchlisted': isShowWatchlisted }"
                type="button"
                :disabled="showWatchlistPending"
                @click="toggleShowWatchlist"
              >
                <div class="default-btn add-to-watchlist">
                  <i class="ion-bookmark"></i> {{ watchlistButtonText }}
                </div>
                <div class="default-btn watchlist">
                  <i class="ion-bookmark"></i> {{ watchlistButtonShortText }}
                </div>
              </button>
            </template>
            <template v-else>
              <a class="fs-btn left-btn" :href="loginPath">
                <div class="default-btn add-to-watchlist">
                  <i class="ion-bookmark"></i> Add to watchlist
                </div>
                <div class="default-btn watchlist">
                  <i class="ion-bookmark"></i> Watchlist
                </div>
              </a>
            </template>
            <template v-if="isAuthenticated">
              <button
                class="fs-btn"
                :class="[
                  { 'btn--favourited': isShowFavourited },
                  'right-btn',
                ]"
                type="button"
                :disabled="showFavouritePending"
                @click="toggleShowFavourite"
              >
                <div class="default-btn fav-btn">
                  <i class="ion-heart"></i> {{ favouriteButtonText }}
                </div>
                <div class="default-btn fav-btn-sm">
                  <i class="ion-heart"></i> {{ favouriteButtonShortText }}
                </div>
              </button>
            </template>
            <template v-else>
              <a class="fs-btn right-btn" :href="loginPath">
                <div class="default-btn fav-btn">
                  <i class="ion-heart"></i> Add to favourites
                </div>
                <div class="default-btn fav-btn-sm">
                  <i class="ion-heart"></i> Favourite
                </div>
              </a>
            </template>
          </div>
        </div>
      </div>
    </div>

    <section v-if="hasLoadedShowPayload" class="mobile g-0 container">
      <div class="row g-0 item-title-mobile">
        <h1 :class="titleLengthClass">
          {{ showTitle }}
          <span class="release-year font-darker-gray weight300">{{ showYearDisplay }}</span>
        </h1>
      </div>
      <div class="row g-0 votes-row justify-content-between">
        <div class="col g-0 rating-text">
          <p class="rate">
            <span>{{ ratingLabelPrefix }}</span>
            <span>{{ ratingLabelValue }}</span>
            <button
              v-if="isShowRated"
              class="rating-reset"
              data-testid="rating-reset-action"
              type="button"
              :disabled="showRatingPending"
              aria-label="Remove rating"
              @click="removeShowRating"
            >
              <svg
                class="rating-reset__icon"
                viewBox="0 0 16 16"
                aria-hidden="true"
                focusable="false"
              >
                <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.2" />
                <path d="M5.2 5.2 10.8 10.8" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
                <path d="M10.8 5.2 5.2 10.8" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
              </svg>
            </button>
          </p>
        </div>
      </div>
      <div class="row mobile-rating">
        <div class="col rating-stars justify-content-center" @mouseleave="resetRatingPreview">
          <template v-if="isAuthenticated">
            <button
              v-for="star in 10"
              :key="`mobile-${star}`"
              class="rating-star-button"
              data-testid="rating-star"
              type="button"
              :disabled="showRatingPending"
              :aria-label="`Rate ${star} out of 10`"
              @mouseenter="previewRating(star)"
              @focus="previewRating(star)"
              @blur="resetRatingPreview"
              @click="setShowRating(star)"
            >
              <i :class="ratingStarClass(star)"></i>
            </button>
          </template>
          <template v-else>
            <a
              v-for="star in 10"
              :key="`mobile-${star}`"
              :href="loginPath"
              data-testid="rating-star"
              :aria-label="`Rate ${star} out of 10`"
              @mouseenter="previewRating(star)"
              @focus="previewRating(star)"
              @blur="resetRatingPreview"
            >
              <i :class="ratingStarClass(star)"></i>
            </a>
          </template>
        </div>
      </div>
      <div class="row g-0">
        <div class="col g-0">
          <div class="buttons buttons--without-calendar">
            <template v-if="isAuthenticated">
              <button
                class="fs-btn left-btn"
                :class="{ 'btn--watchlisted': isShowWatchlisted }"
                type="button"
                :disabled="showWatchlistPending"
                @click="toggleShowWatchlist"
              >
                <div class="default-btn">
                  <i class="ion-bookmark"></i> {{ watchlistButtonShortText }}
                </div>
              </button>
            </template>
            <template v-else>
              <a class="fs-btn left-btn" :href="loginPath">
                <div class="default-btn">
                  <i class="ion-bookmark"></i> Watchlist
                </div>
              </a>
            </template>
            <template v-if="isAuthenticated">
              <button
                class="fs-btn"
                :class="[
                  { 'btn--favourited': isShowFavourited },
                  'right-btn',
                ]"
                type="button"
                :disabled="showFavouritePending"
                @click="toggleShowFavourite"
              >
                <div class="default-btn">
                  <i class="ion-heart"></i> {{ favouriteButtonShortText }}
                </div>
              </button>
            </template>
            <template v-else>
              <a class="fs-btn right-btn" :href="loginPath">
                <div class="default-btn">
                  <i class="ion-heart"></i> Favourite
                </div>
              </a>
            </template>
          </div>
        </div>
      </div>
    </section>
    <section
      v-else
      class="mobile g-0 container mobile-hero-placeholder"
      data-testid="show-mobile-hero-placeholder"
      aria-hidden="true"
    ></section>

    <a class="top-link" id="top"></a>
    <div class="container g-0 mobile-nav">
      <div class="row">
        <div class="col">
          <nav class="nav">
            <ul class="film-nav" data-testid="show-tabs">
              <li>
                <RouterLink :class="{ active: activeSection === 'overview' }" :to="basePath">Overview</RouterLink>
              </li>
              <li>
                <RouterLink
                  class="seasons"
                  :class="{ active: activeSection === 'seasons' }"
                  :to="`${basePath}/seasons`"
                >Seasons</RouterLink>
              </li>
              <li>
                <RouterLink
                  :class="['cast-and-crew', { active: activeSection === 'credits' }]"
                  :to="`${basePath}/credits`"
                >Cast</RouterLink>
                <RouterLink
                  :class="['credits', { active: activeSection === 'credits' }]"
                  :to="`${basePath}/credits`"
                >Cast</RouterLink>
              </li>
              <li>
                <RouterLink :class="{ active: activeSection === 'lists' }" :to="`${basePath}/lists`">Lists</RouterLink>
              </li>
              <li>
                <RouterLink
                  :class="{ active: activeSection === 'comments' }"
                  :to="`${basePath}/comments`"
                >Comments</RouterLink>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>

    <div v-if="activeSection === 'credits'" class="container g-0" data-testid="show-cast-panel">
      <div class="row g-0 film-overview">
        <div class="col-12 col-sm-12 col-md-12 col-lg-10">
          <div class="container g-0">
            <div class="row g-0 credits-title">
              <div class="col g-0">
                <h2 class="sub-heading" data-testid="show-cast-heading">Cast</h2>
              </div>
            </div>
            <div v-if="showCastAvailable" class="row g-0 credits-filter-container">
              <div class="col g-0">
                <p data-testid="show-cast-filter-title">Filter cast by person or character name.</p>
                <div class="credits-filter-field">
                  <input
                    id="show-cast-filter"
                    v-model="showCastFilter"
                    data-testid="show-cast-filter-input"
                    class="credits-filter"
                    type="search"
                    placeholder="Type person or cast member name"
                  >
                </div>
              </div>
            </div>
            <template v-if="showCastAvailable">
              <a v-if="showRegularsSectionVisible" id="regulars"></a>
              <div v-if="showRegularsSectionVisible" class="row g-0 credits-title-section">
                <div class="col g-0">
                  <p data-testid="show-regulars-title">{{ showRegularsTitle }}</p>
                </div>
              </div>
              <div v-if="showRegularsSectionVisible" class="row g-0">
                <div class="col g-0">
                  <div
                    v-for="member in filteredCreditsRegulars"
                    :key="`regular-${member.name}-${member.role}`"
                    class="cast-member-row"
                    data-testid="show-cast-member"
                  >
                    <div class="cast-member-photo">
                      <img :src="member.image" alt="">
                    </div>
                    <div class="names">
                      <div class="cast-member-name">
                        <a :href="member.url || '#'">{{ member.name }}</a>
                      </div>
                      <div v-if="member.role || member.countText" class="cast-character-name">
                        <p v-if="member.role">{{ member.role }}</p>
                        <p v-if="member.countText" class="cast-appearance-count">{{ member.countText }}</p>
                      </div>
                    </div>
                  </div>
                  <div class="back-to-top">
                    <a href="#top" @click.prevent="scrollShowCastToAnchor">
                      Back to top <i class="ion-android-arrow-up"></i>
                    </a>
                  </div>
                </div>
              </div>
            </template>
            <div v-else-if="showLoaded" class="row g-0 credits-empty-row">
              <div class="col g-0">
                <p class="no-cast-message no-credits-message" data-testid="show-no-cast-message">
                  No cast available
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="d-none d-md-block col-lg-2 ad-section">
        </div>
      </div>
    </div>

    <div v-else-if="activeSection === 'seasons'" class="container g-0" data-testid="show-seasons-panel">
      <div class="row g-0 film-overview">
        <div class="col-12 col-sm-12 col-md-12 col-lg-10 seasons-list-container">
          <div class="container g-0 seasons-title-container">
            <div class="row g-0">
              <div class="col g-0">
                <h2 class="seasons-title" data-testid="all-seasons-title">{{ allSeasonsTitle }}</h2>
              </div>
            </div>
          </div>

          <div
            v-if="seasonSortButtonsVisible || seasonViewModeButtonsVisible"
            class="container g-0 seasons-controls-row"
          >
            <div class="row g-0">
              <div class="col g-0">
                <div class="seasons-controls">
                  <div
                    v-if="seasonSortButtonsVisible"
                    class="seasons-sort-controls"
                    data-testid="season-sort-controls-row"
                  >
                    <span class="sort-title">Sort:</span>
                    <button
                      v-for="button in seasonSortButtons"
                      :key="button.key"
                      :class="['season-control-btn', 'season-sort-button', button.active ? 'active' : null]"
                      :data-testid="`season-sort-${button.key}`"
                      type="button"
                      @click="setSeasonSortMode(button.key)"
                    >
                      {{ button.label }}
                    </button>
                  </div>

                  <div
                    v-if="seasonViewModeButtonsVisible"
                    class="seasons-view-controls"
                    data-testid="season-view-controls-row"
                  >
                    <button
                      v-for="button in seasonViewModeButtons"
                      :key="button.key"
                      :class="['season-control-btn', 'season-view-mode-button', button.active ? 'active' : null]"
                      :data-testid="`season-view-mode-${button.key}`"
                      :aria-label="button.label"
                      :title="button.label"
                      type="button"
                      @click="setSeasonListingMode(button.key)"
                    >
                      <ViewModeIcon :mode="button.key" data-testid="season-view-mode-icon" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="container g-0 seasons-filter-container credits-filter-container">
            <div class="row g-0">
              <div class="col g-0">
                <p data-testid="season-filter-title">
                  Filter by season title or number
                </p>
                <div class="credits-filter-field">
                  <input
                    v-model.trim="seasonFilter"
                    data-testid="season-filter-input"
                    type="search"
                    class="credits-filter season-filter-input"
                    placeholder="Type a season title or number"
                  >
                </div>
                <p
                  v-if="showSeasonWatchProgressVisible"
                  class="show-season-watch-progress"
                  data-testid="show-season-watch-progress"
                >{{ showSeasonWatchProgressCountText }} ({{ showSeasonWatchProgressPercentage }}%)<template v-if="showSeasonWatchProgressComplete"> <i class="ion-checkmark" aria-hidden="true"></i></template></p>
              </div>
            </div>
          </div>

          <template v-if="seasonListingMode === '1'">
            <div
              v-for="(season, seasonIndex) in filteredSeasonEntries"
              :key="`season-sm-${seasonIndex}`"
              :class="[
                'row',
                'g-0',
                'single-season-row-sm',
                season.special ? 'special-season-row' : null,
                filteredSeasonEntries[seasonIndex + 1]?.special ? 'before-special-season-row' : null,
              ]"
            >
              <div class="col g-0">
                <div class="container g-0">
                  <div class="row g-0">
                    <div class="col-3 g-0">
                      <div class="single-season-poster">
                        <a :href="season.url || '#'">
                          <img :src="season.posterUrl" :alt="`${showTitle} | ${season.title} poster`">
                        </a>
                      </div>
                    </div>
                    <div class="col-9 g-0">
                      <div class="single-season-specs">
                        <div class="season-title-progress-row">
                          <h3><a :href="season.url || '#'">{{ season.title }}</a></h3>
                          <span
                            v-if="seasonWatchProgressVisible(season)"
                            class="season-watch-progress"
                            data-testid="season-watch-progress-sm"
                          >{{ seasonWatchProgressCountText(season) }} ({{ seasonWatchProgressPercentage(season) }}%)<template v-if="seasonWatchProgressComplete(season)"> <i class="ion-checkmark" aria-hidden="true"></i></template></span>
                        </div>
                        <div v-if="season.overview" class="season-overview">
                          <p>
                            <span data-testid="season-overview-body">{{ displayedSeasonOverview(season) }}</span>
                            <button
                              v-if="hasExpandableSeasonOverview(season)"
                              class="overview-toggle"
                              data-testid="season-overview-toggle"
                              type="button"
                              @click="toggleSeasonOverview(season)"
                            >
                              <span class="overview-toggle__icon" aria-hidden="true">{{ seasonOverviewExpanded(season) ? '▲' : '▼' }}</span>
                            </button>
                          </p>
                        </div>
                        <div class="season-episode-count">
                          <p>
                            {{ season.episodeCountText }}
                            <template v-if="season.yearText">
                              • <span class="season-year">{{ season.yearText }}</span>
                            </template>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="row g-0">
                    <div class="col g-0">
                      <div class="season-episode-count-sm">
                        <p>
                          {{ season.episodeCountText }}
                          <template v-if="season.yearText">
                            • <span class="season-year">{{ season.yearText }}</span>
                          </template>
                        </p>
                      </div>
                      <div v-if="season.stars.length > 0" class="season-stars-list">
                        <span class="stars-title">Stars:</span>
                        <template v-for="(star, index) in season.stars" :key="`${season.title}-${star.name}`">
                          <template v-if="index > 0"> • </template>
                          <a class="season-star" :href="star.url || '#'">{{ star.name }}</a>
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-for="(season, seasonIndex) in filteredSeasonEntries"
              :key="`season-${seasonIndex}`"
              :class="[
                'row',
                'g-0',
                'single-season-row',
                season.special ? 'special-season-row' : null,
                filteredSeasonEntries[seasonIndex + 1]?.special ? 'before-special-season-row' : null,
              ]"
              data-testid="season-entry"
            >
              <div class="col-3 g-0">
                <div class="single-season-poster">
                  <a :href="season.url || '#'">
                    <img :src="season.posterUrl" :alt="`${showTitle} | ${season.title} poster`">
                  </a>
                </div>
              </div>
              <div class="col-9 g-0 single-season-specs">
                <div class="season-title-progress-row">
                  <h3><a :href="season.url || '#'" data-testid="season-title">{{ season.title }}</a></h3>
                  <span
                    v-if="seasonWatchProgressVisible(season)"
                    class="season-watch-progress"
                    data-testid="season-watch-progress"
                  >{{ seasonWatchProgressCountText(season) }} ({{ seasonWatchProgressPercentage(season) }}%)<template v-if="seasonWatchProgressComplete(season)"> <i class="ion-checkmark" aria-hidden="true"></i></template></span>
                </div>
                <div v-if="season.overview" class="season-overview">
                  <p>
                    <span data-testid="season-overview-body">{{ displayedSeasonOverview(season) }}</span>
                    <button
                      v-if="hasExpandableSeasonOverview(season)"
                      class="overview-toggle"
                      data-testid="season-overview-toggle"
                      type="button"
                      @click="toggleSeasonOverview(season)"
                    >
                      <span class="overview-toggle__icon" aria-hidden="true">{{ seasonOverviewExpanded(season) ? '▲' : '▼' }}</span>
                    </button>
                  </p>
                </div>
                <div class="season-episode-count">
                  <p>
                    {{ season.episodeCountText }}
                    <template v-if="season.yearText">
                      • <span class="season-year">{{ season.yearText }}</span>
                    </template>
                  </p>
                </div>
                <div v-if="season.stars.length > 0" class="season-stars-list">
                  <span class="stars-title">Stars:</span>
                  <template v-for="(star, index) in season.stars" :key="`${season.title}-desktop-${star.name}`">
                    <template v-if="index > 0"> • </template>
                    <a class="season-star" :href="star.url || '#'">{{ star.name }}</a>
                  </template>
                </div>
              </div>
            </div>
          </template>

          <div v-else-if="seasonListingMode === '2'" class="season-posters-grid">
            <a
              v-for="season in filteredSeasonEntries"
              :key="`season-poster-${season.title}`"
              class="season-poster-card"
              data-testid="season-poster-card"
              :href="season.url || '#'"
            >
              <img :src="season.posterUrl" :alt="`${showTitle} | ${season.title} poster`">
              <span
                class="season-poster-overlay"
                :class="{ 'season-poster-overlay--with-progress': seasonWatchProgressVisible(season) }"
                data-testid="season-poster-overlay"
              >
                <span class="season-poster-title" data-testid="season-poster-title">{{ season.title }}</span>
                <span
                  v-if="seasonWatchProgressVisible(season)"
                  class="season-poster-progress"
                  data-testid="season-poster-progress"
                >{{ seasonWatchProgressCompactText(season) }}<template v-if="seasonWatchProgressComplete(season)"> <i class="ion-checkmark" aria-hidden="true"></i></template></span>
              </span>
            </a>
          </div>

          <div v-else class="season-text-only-list">
            <div
              v-for="(season, seasonIndex) in filteredSeasonEntries"
              :key="`season-text-only-${season.title}`"
              :class="[
                'season-text-only-row',
                seasonIndex === filteredSeasonEntries.length - 1 ? 'season-text-only-row--last' : null,
              ]"
              data-testid="season-text-only-entry"
            >
              <div class="season-text-only-main">
                <div class="season-text-only-title">
                  <a :href="season.url || '#'">{{ season.title }}</a>
                </div>
                <div class="season-text-only-meta">
                  <span>{{ season.episodeCountText }}</span>
                  <template v-if="season.yearText">
                    <span> • {{ season.yearText }}</span>
                  </template>
                </div>
              </div>
              <span
                v-if="seasonWatchProgressVisible(season)"
                class="season-text-only-progress"
                data-testid="season-text-only-progress"
              >{{ seasonWatchProgressCountText(season) }} ({{ seasonWatchProgressPercentage(season) }}%)<template v-if="seasonWatchProgressComplete(season)"> <i class="ion-checkmark" aria-hidden="true"></i></template></span>
            </div>
          </div>
        </div>

        <div class="d-none d-md-block col-lg-2 ad-section">
        </div>
      </div>
    </div>

    <div v-else-if="activeSection === 'comments'" class="container g-0" data-testid="comments-panel">
      <div class="row g-0 film-overview">
        <div class="col-12 col-sm-12 col-md-12 col-lg-10">
          <CommentsPanel
            :base-path="basePath"
            heading="Discussion"
            :is-active="activeSection === 'comments'"
            target-type="show"
            :target-id="show.id"
            test-id-prefix="show"
          />
        </div>
        <div class="d-none d-md-block col-lg-2 ad-section">
        </div>
      </div>
    </div>

    <div v-else-if="activeSection === 'lists'" class="container g-0" data-testid="lists-panel">
      <div class="row g-0 film-overview">
        <div class="col-12 col-sm-12 col-md-12 col-lg-10">
          <div class="container g-0 film-lists-panel">
            <div class="row g-0 credits-title">
              <div class="col g-0">
                <h2 class="sub-heading" data-testid="show-lists-heading">{{ showListsHeading }}</h2>
              </div>
            </div>

            <div class="row g-0 list-addition-row">
              <div class="col g-0">
                <template v-if="isAuthenticated">
                  <button
                    class="btn-sm list-addition-btn"
                    data-testid="show-list-open"
                    type="button"
                    @click="openShowListModal"
                  >
                    + Add to list
                  </button>
                </template>
                <template v-else>
                  <a class="btn-sm list-addition-btn" data-testid="show-list-open" :href="loginPath">+ Add to list</a>
                </template>
              </div>
            </div>

            <p v-if="showListsPageError" class="film-lists-empty" data-testid="show-lists-error">
              {{ showListsPageError }}
            </p>
            <p v-else-if="showListsPageLoading" class="film-lists-empty" data-testid="show-lists-loading">
              Loading lists...
            </p>
            <p
              v-else-if="showListsPageLoaded && showLists.length === 0"
              class="film-lists-empty"
              data-testid="show-lists-empty"
            >
              This show doesn't belong any list, yet.
            </p>

            <TransitionGroup
              v-else-if="showLists.length > 0"
              name="overview-list"
              tag="div"
              class="film-list-stack film-list-stack--page"
            >
              <div
                v-for="(showList, showListIndex) in showLists"
                :key="showList.id"
                class="film-list film-list--page"
                data-testid="show-list-page-entry"
              >
                <button
                  v-if="showListIndex === 0 && showListSuccessTitle"
                  class="film-list-success-notice"
                  :class="{ 'film-list-success-notice--dismissing': showListSuccessDismissing }"
                  data-testid="show-list-success-notice"
                  type="button"
                  @click="dismissShowListSuccessNotice"
                >
                  This show was added to list "{{ showListSuccessTitle }}".
                </button>
                <div class="list-posters">
                  <div v-for="item in showList.items" :key="item.id" class="list-poster">
                    <a :href="showList.href"><img :src="item.posterUrl" :alt="`${item.title} poster`"></a>
                  </div>
                </div>
                <a class="list-title" :href="showList.href">{{ showList.title }}</a>
                <CuratedListLabel v-if="showList.is_curated" />
                  <p v-else-if="showList.official" class="list-owner official-list">Official list</p>
                <p v-else class="list-owner">by <a :href="showList.ownerHref">{{ showList.ownerName }}</a></p>
              </div>

              <nav
                v-if="showShowListsPagination"
                key="show-lists-pagination"
                class="show-lists-pagination show-lists-pagination--with-mobile"
                aria-label="Show lists pagination"
              >
                <ul class="pagination mb-0">
                  <li class="page-item" :class="{ disabled: !canGoToPreviousShowListsPage || showListsPageLoading }">
                    <button
                      class="page-link"
                      data-testid="show-lists-prev"
                      type="button"
                      @click="goToShowListsPage(showListsPageNumber - 1)"
                    >
                      Previous
                    </button>
                  </li>
                  <li
                    v-for="pageItem in showListsPages"
                    :key="`show-lists-${pageItem.key}`"
                    class="page-item"
                    :class="{ active: pageItem.page === showListsPageNumber, disabled: pageItem.ellipsis }"
                  >
                    <span v-if="pageItem.ellipsis" class="page-link" aria-hidden="true">
                      {{ pageItem.label }}
                    </span>
                    <button
                      v-else
                      class="page-link"
                      :data-testid="`show-lists-page-${pageItem.page}`"
                      type="button"
                      :aria-label="`Go to page ${pageItem.page}`"
                      @click="goToShowListsPage(pageItem.page)"
                    >
                      {{ pageItem.label }}
                    </button>
                  </li>
                  <li class="page-item" :class="{ disabled: !canGoToNextShowListsPage || showListsPageLoading }">
                    <button
                      class="page-link"
                      data-testid="show-lists-next"
                      type="button"
                      @click="goToShowListsPage(showListsPageNumber + 1)"
                    >
                      Next
                    </button>
                  </li>
                </ul>
                <CompactMobilePagination
                  :current-page="showListsPageNumber"
                  :total-pages="showListsNumPages"
                  :loading="showListsPageLoading"
                  test-id-prefix="show-lists"
                  @page-change="goToShowListsPage"
                />
              </nav>
            </TransitionGroup>
          </div>
        </div>

        <div class="d-none d-md-block col-lg-2 ad-section">
        </div>
      </div>
    </div>

    <div v-else class="container g-0" data-testid="show-overview-panel">
      <div v-if="showBulkWatchButtonVisible" class="row g-0 show-bulk-watch-row">
        <div class="col g-0 show-bulk-watch-row__content">
          <button
            class="show-bulk-watch-button"
            data-testid="show-bulk-watch-action"
            type="button"
            :disabled="showBulkWatchPending"
            @click="markShowAsWatched"
          >
            Mark show as watched
          </button>
        </div>
      </div>
      <div class="row g-0 film-overview">
        <div class="col-12 col-sm-12 col-md-8 col-lg-7">
          <div class="film-data film__main">
            <div class="overview">
              <p data-testid="show-overview-text">
                <span data-testid="show-overview-body">{{ displayedShowOverview }}</span>
                <button
                  v-if="hasExpandableShowOverview"
                  class="overview-toggle"
                  data-testid="show-overview-toggle"
                  type="button"
                  @click="toggleShowOverview"
                >
                  <span class="overview-toggle__icon" aria-hidden="true">{{ showOverviewExpanded ? '▲' : '▼' }}</span>
                </button>
              </p>
              <a
                v-if="canAccessBadmin"
                class="more-link show-admin-link"
                data-testid="show-admin-link"
                :href="showAdminUrl"
              >
                Admin
              </a>
            </div>

            <div
              v-if="nextEpisode"
              class="show-next-episode-mobile-section"
              data-testid="show-next-episode-mobile-section"
            >
              <p
                class="show-next-episode-heading"
                data-testid="show-next-episode-mobile-heading"
              >
                Watch next
              </p>
              <a
                class="show-next-episode show-next-episode--mobile"
                :href="nextEpisode.url || '#'"
                data-testid="show-next-episode-mobile"
              >
                <img
                  class="show-next-episode__image"
                  :src="nextEpisode.posterUrl"
                  alt=""
                  data-testid="show-next-episode-mobile-image"
                >
                <span
                  class="show-next-episode__overlay"
                  data-testid="show-next-episode-mobile-overlay"
                >
                  <span class="show-next-episode__meta" data-testid="show-next-episode-mobile-meta">
                    {{ nextEpisode.seasonEpisodeText }}
                  </span>
                  <span class="show-next-episode__title" data-testid="show-next-episode-mobile-title">
                    {{ nextEpisode.title }}
                  </span>
                </span>
              </a>
            </div>

            <div
              :class="[
                'middle-section-mobile',
                'g-0',
                'container',
                nextEpisode ? 'middle-section-mobile--after-next-episode' : null,
              ]"
            >
              <div class="row g-0">
                <div class="col g-0">
                  <div class="title-section">
                    <div>
                      <p>Latest seasons</p>
                    </div>
                    <div>
                      <a
                        class="more-link"
                        data-testid="mobile-latest-seasons-all-link"
                        :href="`${basePath}/seasons`"
                      >
                        {{ mobileLatestSeasonsAllLinkText }}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              <div class="row g-0 latest-seasons-list">
                <div
                  v-for="season in latestSeasons"
                  :key="`latest-season-${season.title}`"
                  class="col-3 g-0"
                  data-testid="latest-season"
                >
                  <a :href="season.url || '#'">
                    <img
                      class="latest-season-cover"
                      :src="season.posterUrl"
                      :alt="`${showTitle}: ${season.title} poster`"
                    >
                  </a>
                  <div class="latest-season">
                    <a :href="season.url || '#'">{{ season.title }}</a>
                    <p>{{ season.episodeCountText }}</p>
                  </div>
                </div>
              </div>
              <div class="row g-0">
                <div class="col g-0">
                  <div class="latest-seasons-list-2">
                    <div v-for="season in latestSeasons" :key="`latest-season-grid-${season.title}`">
                    <div>
                        <a :href="season.url || '#'">
                          <img
                            class="latest-season-cover"
                            :src="season.posterUrl"
                            :alt="`${showTitle}: ${season.title} poster`"
                          >
                        </a>
                      </div>
                      <div class="latest-season">
                        <a :href="season.url || '#'">{{ season.title }}</a>
                        <p>{{ season.episodeCountText }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="film-data-section main-cast">
              <div class="title-section">
                <div>
                  <p>Main cast</p>
                </div>
                <div>
                  <a class="more-link" :href="`${basePath}/credits`">Full cast &gt;</a>
                </div>
              </div>
              <div class="cast-list">
                <p v-if="showLoaded && mainCast.length === 0" class="no-cast-message" data-testid="no-cast-message">
                  No main cast available.
                </p>
                <div
                  v-for="(castRow, rowIndex) in mainCastRows"
                  :key="`cast-row-${rowIndex}`"
                  class="cast-row"
                >
                  <div
                    v-for="cast in castRow"
                    :key="`${cast.actor}-${cast.role}-${cast.url}`"
                    class="cast-member"
                    data-testid="cast-member"
                  >
                    <div class="cast-image">
                      <a :href="cast.url || '#'"><img :src="cast.image" alt=""></a>
                    </div>
                    <div class="cast-name">
                      <a :href="cast.url || '#'">{{ cast.actor }}</a>
                      <p v-if="cast.role">{{ cast.role }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="film-data-section lists">
              <div class="title-section">
                <div>
                  <p>Lists with this show</p>
                </div>
                <div>
                  <a class="more-link" :href="`${basePath}/lists`">
                    More lists <span class="with-this-film">with this show </span>&gt;
                  </a>
                </div>
              </div>
              <TransitionGroup
                v-if="overviewLists.length > 0"
                name="overview-list"
                tag="div"
                class="film-list-stack"
              >
                <div
                  v-for="(showList, showListIndex) in overviewLists"
                  :key="showList.id"
                  class="film-list"
                  data-testid="overview-show-list"
                >
                  <button
                    v-if="showListIndex === 0 && showListSuccessTitle"
                    class="film-list-success-notice"
                    :class="{ 'film-list-success-notice--dismissing': showListSuccessDismissing }"
                    data-testid="show-list-success-notice"
                    type="button"
                    @click="dismissShowListSuccessNotice"
                  >
                    This show was added to list "{{ showListSuccessTitle }}".
                  </button>
                  <div class="list-covers">
                    <div v-for="item in showList.items" :key="item.id" class="list-cover">
                      <a :href="showList.href">
                        <img :src="item.posterUrl" :alt="`${item.title} poster`">
                        <span
                          v-if="item.showPlaceholderLabel"
                          class="film-list-placeholder-title"
                          data-testid="show-list-placeholder-title"
                        >
                          {{ item.placeholderLabel }}
                        </span>
                      </a>
                    </div>
                  </div>
                  <p><a class="list-title" :href="showList.href">{{ showList.title }}</a></p>
                  <CuratedListLabel v-if="showList.is_curated" />
                  <p v-else-if="showList.official" class="list-owner official-list">Official list</p>
                  <p v-else class="list-owner">by <a :href="showList.ownerHref">{{ showList.ownerName }}</a></p>
                </div>
              </TransitionGroup>
              <p
                v-else-if="showLoaded"
                class="film-lists-empty"
                data-testid="overview-show-lists-empty"
              >
                This show doesn't belong any list, yet.
              </p>
              <div class="add-to-list">
                <div>
                  <template v-if="isAuthenticated">
                    <button
                      class="btn-sm"
                      data-testid="show-list-open"
                      type="button"
                      @click="openShowListModal"
                    >
                      + Add to list
                    </button>
                  </template>
                  <template v-else>
                    <a class="btn-sm" data-testid="show-list-open" :href="loginPath">+ Add to list</a>
                  </template>
                </div>
              </div>
            </div>

            <div class="film-data-section discussion">
              <div class="title-section">
                <div>
                  <p>Latest comments</p>
                </div>
                <div><a class="more-link" :href="`${basePath}/comments`">View comments &gt;</a></div>
              </div>
            </div>
            <div class="container g-0 comments">
              <div class="row g-0">
                <div class="col g-0">
                  <template v-if="latestOverviewComments.length > 0">
                    <div
                      v-for="comment in latestOverviewComments"
                      :key="comment.id"
                      class="comment-container"
                      data-testid="comment-card"
                    >
                      <div class="comment-author">
                        <a class="comment-owner" :href="comment.userHref">{{ comment.username }}</a> wrote:
                      </div>
                      <div class="comment-date">{{ overviewCommentTimeText(comment) }}</div>
                      <div class="comment-body">
                        <blockquote>
                          {{ overviewCommentPreviewText(comment) }}
                        </blockquote>
                        <a
                          v-if="overviewCommentIsTruncated(comment)"
                          class="more-link comment-preview-link"
                          :href="`${basePath}/comments`"
                        >
                          View full comment
                        </a>
                      </div>
                    </div>
                  </template>
                  <p
                    v-else-if="latestOverviewCommentsLoaded"
                    class="comment-empty-copy"
                    data-testid="latest-comments-empty"
                  >
                    No comments, yet.
                    <a class="more-link" :href="`${basePath}/comments`">Be the first to comment.</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="d-none d-sm-block col-lg-3 col-md-4 middle-section">
          <div class="seasons-list container g-0">
            <p
              v-if="nextEpisode"
              class="show-next-episode-heading"
              data-testid="show-next-episode-heading"
            >
              Watch next
            </p>
            <a
              v-if="nextEpisode"
              class="show-next-episode"
              :href="nextEpisode.url || '#'"
              data-testid="show-next-episode"
            >
              <img
                class="show-next-episode__image"
                :src="nextEpisode.posterUrl"
                alt=""
                data-testid="show-next-episode-image"
              >
              <span
                class="show-next-episode__overlay"
                data-testid="show-next-episode-overlay"
              >
                <span class="show-next-episode__meta" data-testid="show-next-episode-meta">
                  {{ nextEpisode.seasonEpisodeText }}
                </span>
                <span class="show-next-episode__title" data-testid="show-next-episode-title">
                  {{ nextEpisode.title }}
                </span>
              </span>
            </a>
            <div class="title-section row g-0">
              <div class="col g-0">
                <div class="seasons-list-title">
                  <div>
                    <p data-testid="seasons-title">{{ seasonsTitle }}</p>
                  </div>
                  <div>
                    <a class="more-link" data-testid="desktop-seasons-top-link" :href="`${basePath}/seasons`">
                      {{ desktopSeasonsTopLinkText }}
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div class="row g-0">
              <div class="col g-0 show-episodes-count-container">
                <div class="show-episodes-count-text">
                  <p data-testid="total-episode-count">{{ desktopShowEpisodeSummaryText }}</p>
                </div>
              </div>
            </div>
            <div
              v-for="(season, seasonIndex) in overviewSeasons"
              :key="season.title"
              class="row g-0"
              data-testid="overview-season"
            >
              <div class="col g-0">
                <div
                  :class="[
                    'single-season-container',
                    season.special ? 'specials-season' : null,
                    !overviewHasSpecials && seasonIndex === overviewSeasons.length - 1
                      ? 'single-season-container--last-without-specials'
                      : null,
                  ]"
                >
                  <div :class="['season-cover-container', seasonIndex === 0 ? 'first-season-cover-container' : null]">
                    <a :href="season.url || '#'">
                      <img class="season-cover" :src="season.posterUrl" alt="">
                    </a>
                  </div>
                  <div class="season-title-container">
                    <div>
                      <a class="season-title-link" :href="season.url || '#'">{{ season.title }}</a>
                    </div>
                    <div>
                      <p class="episode-count-text">{{ season.episodeCountText }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="showHasMoreThanTenSeasons && useAllSeasonsDesktopLink" class="row g-0">
              <div class="col g-0 overview-seasons-footer-link">
                <a class="more-link" data-testid="desktop-seasons-footer-link" :href="`${basePath}/seasons`">
                  All seasons &gt;
                </a>
              </div>
            </div>
          </div>
        </div>
        <div class="d-none d-md-block col-lg-2 ad-section">
        </div>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="showListModalOpen"
        class="film-watch-modal-overlay"
        data-testid="show-list-modal-overlay"
      >
        <button
          class="film-watch-modal-backdrop"
          type="button"
          aria-label="Close add to list dialog"
          :disabled="showListModalPending"
          @click="closeShowListModal"
        ></button>
        <div
          class="film-watch-modal film-list-modal"
          data-testid="show-list-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="show-list-modal-title"
          @click.stop
        >
          <div class="film-watch-modal__header">
            <h2 id="show-list-modal-title" class="film-watch-modal__title film-list-modal__title">
              Add "{{ showTitle }}"<br>to list:
            </h2>
            <button
              class="film-watch-modal__close"
              data-testid="show-list-close"
              type="button"
              aria-label="Close add to list dialog"
              :disabled="showListModalPending"
              @click="closeShowListModal"
            >
              <svg class="film-watch-modal__close-icon" viewBox="0 0 20 20" aria-hidden="true">
                <circle cx="10" cy="10" r="8.5" fill="none" stroke="currentColor" stroke-width="1.4" />
                <path
                  d="M7 7 L13 13 M13 7 L7 13"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.4"
                  stroke-linecap="round"
                />
              </svg>
            </button>
          </div>

          <p
            v-if="showListModalError"
            class="film-watch-modal__error"
            data-testid="show-list-error"
          >
            {{ showListModalError }}
          </p>

          <form
            v-if="!showListCreateMode"
            class="film-watch-modal__form"
            @submit.prevent="submitShowListAddition"
          >
            <section class="film-watch-modal__section">
              <div class="film-watch-modal__field">
                <span>List</span>
                <div class="film-list-picker" @click.stop @mousedown.stop>
                  <button
                    class="film-list-picker__toggle"
                    :class="{ 'film-list-picker__toggle--open': showListPickerOpen }"
                    data-testid="show-list-select"
                    type="button"
                    :disabled="showListModalPending || showListModalLoading"
                    @click.stop="showListPickerOpen = !showListPickerOpen"
                  >
                    <span class="film-list-picker__label">{{ selectedShowListLabel }}</span>
                    <span class="film-list-picker__icon ion-chevron-down" aria-hidden="true"></span>
                  </button>
                  <div
                    v-if="showListPickerOpen"
                    class="film-list-picker__menu"
                    data-testid="show-list-picker-menu"
                  >
                    <button
                      class="film-list-picker__option"
                      :class="{ 'film-list-picker__option--selected': !showListSelectedListId }"
                      data-testid="show-list-option-empty"
                      type="button"
                      @click.stop="selectShowList('')"
                    >
                      Choose list...
                    </button>
                    <button
                      v-for="showList in showListOptions"
                      :key="showList.id"
                      class="film-list-picker__option"
                      :class="{ 'film-list-picker__option--selected': String(showList.id) === showListSelectedListId }"
                      :data-testid="`show-list-option-${showList.id}`"
                      type="button"
                      @click.stop="selectShowList(String(showList.id))"
                    >
                      {{ showList.title }}
                    </button>
                  </div>
                </div>
                <select
                  v-model="showListSelectedListId"
                  :disabled="showListModalPending || showListModalLoading"
                  class="film-list-picker__native"
                  tabindex="-1"
                  aria-hidden="true"
                >
                  <option value="">Choose list...</option>
                  <option
                    v-for="showList in showListOptions"
                    :key="showList.id"
                    :value="String(showList.id)"
                  >
                    {{ showList.title }}
                  </option>
                </select>
              </div>
            </section>

            <div class="film-watch-modal__actions film-list-modal__actions">
              <button
                class="film-watch-modal__action film-watch-modal__action--secondary"
                data-testid="show-list-create-toggle"
                type="button"
                :disabled="showListModalPending || showListModalLoading"
                @click="openShowListCreateMode"
              >
                Create new list
              </button>
              <div class="film-watch-modal__action-group">
                <button
                  class="film-watch-modal__action film-watch-modal__action--secondary"
                  data-testid="show-list-cancel"
                  type="button"
                  :disabled="showListModalPending"
                  @click="closeShowListModal"
                >
                  Cancel
                </button>
                <button
                  class="film-watch-modal__action"
                  data-testid="show-list-add-submit"
                  type="submit"
                  :disabled="showListModalPending || showListModalLoading || !showListSelectedListId"
                >
                  Add to list
                </button>
              </div>
            </div>
          </form>

          <form
            v-else
            class="film-watch-modal__form"
            @submit.prevent="submitShowListCreate"
          >
            <section class="film-watch-modal__section">
              <div class="film-watch-modal__section-header">
                <h3>Create a new list</h3>
              </div>
              <label class="film-watch-modal__field" for="show-list-create-title">
                <span>Title</span>
                <input
                  id="show-list-create-title"
                  v-model.trim="showListCreateTitle"
                  class="film-list-modal__input"
                  data-testid="show-list-create-title"
                  type="text"
                  maxlength="255"
                  :disabled="showListModalPending"
                >
              </label>
              <label class="film-watch-modal__field" for="show-list-create-description">
                <span>Description</span>
                <textarea
                  id="show-list-create-description"
                  v-model="showListCreateDescription"
                  class="film-watch-modal__textarea"
                  data-testid="show-list-create-description"
                  rows="5"
                  :disabled="showListModalPending"
                ></textarea>
              </label>
              <label class="film-watch-modal__checkbox">
                <input
                  v-model="showListCreatePublic"
                  data-testid="show-list-create-public"
                  type="checkbox"
                  :disabled="showListModalPending"
                >
                <span>Public</span>
              </label>
            </section>

            <div class="film-watch-modal__actions">
              <div class="film-watch-modal__action-group">
                <button
                  class="film-watch-modal__action film-watch-modal__action--secondary"
                  data-testid="show-list-create-cancel"
                  type="button"
                  :disabled="showListModalPending"
                  @click="closeShowListModal"
                >
                  Cancel
                </button>
                <button
                  class="film-watch-modal__action"
                  data-testid="show-list-create-submit"
                  type="submit"
                  :disabled="showListModalPending"
                >
                  Add to list
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Teleport>

    <div
      v-if="showBulkWatchModalOpen"
      class="show-bulk-watch-modal"
      data-testid="show-bulk-watch-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="show-bulk-watch-modal-title"
    >
      <button
        class="show-bulk-watch-modal__backdrop"
        type="button"
        aria-label="Close bulk watch status"
        :disabled="showBulkWatchPending"
        @click="closeShowBulkWatchModal"
      ></button>
      <div class="show-bulk-watch-modal__card">
        <p id="show-bulk-watch-modal-title" class="show-bulk-watch-modal__title">
          {{ showBulkWatchModalTitle }}
        </p>
        <p class="show-bulk-watch-modal__body">
          {{ showBulkWatchModalBody }}
        </p>
        <p
          v-if="showBulkWatchSeasonZeroNoticeVisible"
          class="show-bulk-watch-modal__note"
          data-testid="show-bulk-watch-season-zero-note"
        >
          Specials were not added.
        </p>
        <div
          class="show-bulk-watch-modal__progress"
          :class="{ 'show-bulk-watch-modal__progress--complete': showBulkWatchCompleted }"
          aria-hidden="true"
        >
          <span class="show-bulk-watch-modal__progress-bar"></span>
        </div>
        <div v-if="showBulkWatchCompleted" class="show-bulk-watch-modal__actions">
          <button
            class="show-bulk-watch-modal__close"
            data-testid="show-bulk-watch-close"
            type="button"
            @click="closeShowBulkWatchModal"
          >
            Close
          </button>
        </div>
      </div>
    </div>

    <MobileSearchOverlay :open="mobileSearchOpen" @close="closeMobileSearchOverlay" />
  </main>
</template>

<script setup>
import CuratedListLabel from '../components/CuratedListLabel.vue'
import { usePageViewTracking } from '../analytics/pageViews'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import '../assets/single-film/bootstrap-lite.css'
import '../assets/single-film/styles.css'
import '../assets/single-show/show-detail.css'
import '../assets/single-show/seasons.css'
import CommentsPanel from '../components/CommentsPanel.vue'
import CompactMobilePagination from '../components/CompactMobilePagination.vue'
import HeroTopPanel from '../components/HeroTopPanel.vue'
import MobileSearchOverlay from '../components/MobileSearchOverlay.vue'
import ViewModeIcon from '../components/ViewModeIcon'
import { compactPaginationItems } from '../utils/pagination'
import NotFoundView from './NotFoundView.vue'
import { useAuthStore } from '../stores/auth'
import { buildLoginPath } from '../utils/auth'
import { clipOverviewWithSuffix, hasExpandableOverview } from '../utils/overview'
import { buildDocumentTitle, setDocumentTitle } from '../utils/documentTitle'
import { buildMetaDescription, useMetaDescription } from '../utils/metaDescription'

const route = useRoute()
const capturePageView = usePageViewTracking()
const auth = useAuthStore()
auth.initFromStorage()
const slug = computed(() => route.params.slug || 'game-of-thrones')
const basePath = computed(() => `/shows/${slug.value}`)
const loginPath = computed(() => buildLoginPath(route.fullPath || basePath.value))
const isAuthenticated = computed(() => auth.isAuthenticated.value)
const authUsername = computed(() => auth.state.username || 'Account')
const activeSection = computed(() => {
  const section = route.params.section || 'overview'
  return section === 'discussion' ? 'comments' : section
})
const imageBase = '/examples/new-ui/single-show/images'
const hoverRating = ref(0)
const showLoaded = ref(false)
const showPosterStatus = ref('loading')
const showNotFound = ref(false)
const seasonFilter = ref('')
const seasonSortMode = ref('newest')
const seasonListingMode = ref('1')
const showCastFilter = ref('')
const showOverviewExpanded = ref(false)
const expandedSeasonOverviews = ref({})
const useAllSeasonsDesktopLink = ref(false)
const mobileSearchOpen = ref(false)
const latestOverviewCommentsLoaded = ref(false)
const latestOverviewComments = ref([])
const showListModalOpen = ref(false)
const showListModalPending = ref(false)
const showListModalLoading = ref(false)
const showListModalError = ref('')
const showListCreateMode = ref(false)
const showListOptions = ref([])
const showListSuccessTitle = ref('')
const showListSuccessDismissing = ref(false)
const showListSelectedListId = ref('')
const showListPickerOpen = ref(false)
const showListCreateTitle = ref('')
const showListCreateDescription = ref('')
const showListCreatePublic = ref(true)
const showListsPageLoading = ref(false)
const showListsPageLoaded = ref(false)
const showListsPageError = ref('')
const showListsPageNumber = ref(1)
const showListsPageSize = ref(10)
const showListsPageCount = ref(0)
const showListsNumPages = ref(1)
const showListsPageEntries = ref([])
const showWatchlistPending = ref(false)
const showWatchlisted = ref(false)
const showBulkWatchPending = ref(false)
const showBulkWatchModalOpen = ref(false)
const showBulkWatchCompleted = ref(false)
const showBulkWatchSkippedSeasonZeroCount = ref(0)
const showWatchedEpisodeCount = ref(0)
const showEpisodeCount = ref(0)
const showWatchStatusLoaded = ref(false)
const showSeasonWatchProgress = ref({})
const showDetailRequestPending = ref(false)
const embeddedViewerStateSlug = ref('')
const showFavouritePending = ref(false)
const showFavourited = ref(false)
const showRatingPending = ref(false)
const showRating = ref(null)
const showOverviewLoaded = ref(false)
const showSeasonsLoaded = ref(false)
const showCreditsLoadedSlug = ref('')

const seasonOverviewClipLimit = 150
const posterPlaceholderImage = '/static/films/img/placeholder-film-camera.svg'
const episodePlaceholderImage = '/static/films/img/placeholder-episode-tv.svg'
const defaultBackgroundImage = '/media/films/backgrounds/default-bg.jpg'

function openMobileSearchOverlay() {
  mobileSearchOpen.value = true
}

function closeMobileSearchOverlay() {
  mobileSearchOpen.value = false
}

function createEmptyShowState() {
  return {
    id: null,
    title: '',
    startYear: null,
    endYear: null,
    posterUrl: '',
    backgroundUrl: '',
    mobileBackgroundUrl: '',
    overview: '',
    seasonsTitle: 'Seasons (0)',
    allSeasonsTitle: 'Seasons (0)',
    totalEpisodeCount: 0,
    totalEpisodeCountText: '0 episodes',
    overviewSeasons: [],
    latestSeasons: [],
    seasonEntries: [],
    mainCast: [],
    creditsRegulars: [],
    seasonCount: 0,
    overviewLists: [],
    nextEpisode: null,
    canAccessBadmin: false,
  }
}

function createFallbackShowState(currentSlug) {
  return createEmptyShowState(currentSlug)
}

const show = ref(createFallbackShowState(slug.value))
const hasLoadedShowPayload = computed(() => showLoaded.value && !showNotFound.value)

const assets = {
  logo: '/static/spa/branding/logo.png',
}

const showTitle = computed(() => show.value.title || '')
const documentTitle = computed(() => {
  const years = formatShowYears(show.value)
  return buildDocumentTitle([years ? `${showTitle.value} ${years}` : showTitle.value], activeSection.value)
})
const showOverview = computed(() => show.value.overview || '')
const hasExpandableShowOverview = computed(() => hasExpandableOverview(showOverview.value))
const displayedShowOverview = computed(() => {
  if (!hasExpandableShowOverview.value || showOverviewExpanded.value) {
    return showOverview.value
  }
  return clipOverviewWithSuffix(showOverview.value, undefined, '')
})
const canAccessBadmin = computed(() => show.value.canAccessBadmin === true && Boolean(show.value.id))
const showAdminUrl = computed(() => `/badmin/shows/${show.value.id}/edit`)
const backgroundUrl = computed(() => show.value.backgroundUrl || '')
const mobileBackgroundUrl = computed(() => show.value.mobileBackgroundUrl || '')
const showPosterLoading = computed(() => showPosterStatus.value === 'loading')
const showPosterImageSrc = computed(() => show.value.posterUrl || '')
const showPosterFrameClasses = computed(() => [
  'image-load-frame',
  'image-load-frame--poster',
  showPosterStatus.value === 'loading' ? 'image-load-frame--loading' : null,
  showPosterStatus.value === 'loaded' ? 'image-load-frame--loaded' : null,
  showPosterStatus.value === 'fallback' ? 'image-load-frame--fallback' : null,
])
const showPosterImageClasses = computed(() => [
  'hero__poster',
  'image-load-frame__image',
  showPosterStatus.value !== 'loading' ? 'image-load-frame__image--visible' : null,
])
const seasonsTitle = computed(() => show.value.seasonsTitle || 'Seasons (0)')
const allSeasonsTitle = computed(() => show.value.allSeasonsTitle || 'Seasons (0)')
const totalEpisodeCountText = computed(() => show.value.totalEpisodeCountText || '0 episodes')
const nextEpisode = computed(() => show.value.nextEpisode)
const desktopShowEpisodeSummaryText = computed(() => totalEpisodeCountText.value)
const showSeasonWatchProgressTotal = computed(() => (
  showEpisodeCount.value > 0
    ? showEpisodeCount.value
    : (Number.isInteger(show.value.totalEpisodeCount) ? show.value.totalEpisodeCount : 0)
))
const showSeasonWatchProgressVisible = computed(() => (
  isAuthenticated.value
    && showWatchStatusLoaded.value
    && showSeasonWatchProgressTotal.value > 0
))
const showSeasonWatchProgressPercentage = computed(() => {
  const total = showSeasonWatchProgressTotal.value
  if (total <= 0) return 0
  return Math.min(100, Math.ceil((showWatchedEpisodeCount.value / total) * 100))
})
const showSeasonWatchProgressComplete = computed(() => (
  showSeasonWatchProgressTotal.value > 0
    && showWatchedEpisodeCount.value >= showSeasonWatchProgressTotal.value
))
const showSeasonWatchProgressCountText = computed(() => (
  `${showWatchedEpisodeCount.value}/${showSeasonWatchProgressTotal.value}`
))
const overviewSeasons = computed(() => show.value.overviewSeasons || [])
const overviewHasSpecials = computed(() => overviewSeasons.value.some((season) => season.special))
const latestSeasons = computed(() => show.value.latestSeasons || [])
const seasonEntries = computed(() => show.value.seasonEntries || [])
const overviewLists = computed(() =>
  Array.isArray(show.value.overviewLists) ? show.value.overviewLists : [],
)
const showListsHeading = computed(() => `Lists with this show (${showListsPageCount.value})`)
const showLists = computed(() => showListsPageEntries.value)
const showShowListsPagination = computed(() => showListsNumPages.value > 1)
const canGoToPreviousShowListsPage = computed(() => showListsPageNumber.value > 1)
const canGoToNextShowListsPage = computed(() => showListsPageNumber.value < showListsNumPages.value)
const showListsPages = computed(() => (
  compactPaginationItems(showListsPageNumber.value, showListsNumPages.value)
))
const selectedShowListLabel = computed(() => {
  const selectedId = String(showListSelectedListId.value || '').trim()
  if (!selectedId) {
    return 'Choose list...'
  }
  const selectedList = showListOptions.value.find((entry) => String(entry.id) === selectedId)
  return selectedList?.title || 'Choose list...'
})
const seasonEntryCount = computed(() => seasonEntries.value.length)
const seasonCount = computed(() => Number.parseInt(show.value.seasonCount, 10) || 0)
const showHasMoreThanTenSeasons = computed(() => seasonCount.value > 10)
const seasonSortButtonsVisible = computed(() => seasonEntryCount.value > 1)
const seasonViewModeButtonsVisible = computed(() => seasonEntryCount.value > 0)
const seasonSortButtons = computed(() => [
  { key: 'oldest', label: 'Oldest', active: seasonSortMode.value === 'oldest' },
  { key: 'newest', label: 'Newest', active: seasonSortMode.value === 'newest' },
])
const seasonViewModeButtons = computed(() => [
  { key: '1', label: 'Detailed view', active: seasonListingMode.value === '1' },
  { key: '2', label: 'Poster grid view', active: seasonListingMode.value === '2' },
  { key: '3', label: 'Compact list view', active: seasonListingMode.value === '3' },
])
const creditsRegulars = computed(() => show.value.creditsRegulars || [])
const showCastFilterTerm = computed(() => showCastFilter.value.trim().toLowerCase())
const filteredCreditsRegulars = computed(() => filterShowCastMembers(creditsRegulars.value))
const showCastAvailable = computed(() => creditsRegulars.value.length > 0)
const showRegularsSectionVisible = computed(() => filteredCreditsRegulars.value.length > 0)
const showRegularsTitle = computed(() => `Regulars (${filteredCreditsRegulars.value.length})`)
const filteredSeasonEntries = computed(() => {
  const query = (seasonFilter.value || '').trim().toLowerCase()
  const filteredEntries = !query
    ? [...seasonEntries.value]
    : seasonEntries.value.filter((season) => {
        const title = (season.title || '').toLowerCase()
        if (title.includes(query)) return true

        return (season.stars || []).some((star) => (star.name || '').toLowerCase().includes(query))
      })

  filteredEntries.sort((left, right) => {
    if (left.special !== right.special) {
      return left.special ? 1 : -1
    }
    const leftNumber = Number.isInteger(left.number) ? left.number : 0
    const rightNumber = Number.isInteger(right.number) ? right.number : 0
    if (seasonSortMode.value === 'oldest') {
      return leftNumber - rightNumber || left.title.localeCompare(right.title)
    }
    return rightNumber - leftNumber || left.title.localeCompare(right.title)
  })

  return filteredEntries
})
const mainCast = computed(() => show.value.mainCast || [])
const isShowWatchlisted = computed(() => showWatchlisted.value)
const isShowFavourited = computed(() => showFavourited.value)
const isShowRated = computed(() => Number.isInteger(showRating.value) && showRating.value >= 1 && showRating.value <= 10)
const showBulkWatchButtonVisible = computed(
  () => isAuthenticated.value
    && showEpisodeCount.value > 0
    && showWatchedEpisodeCount.value === 0
    && !showBulkWatchPending.value,
)
const showBulkWatchModalTitle = computed(() => (
  showBulkWatchCompleted.value ? 'Show marked as watched' : 'Marking show as watched'
))
const showBulkWatchModalBody = computed(() => (
  showBulkWatchCompleted.value
    ? 'All the episodes marked as watched.'
    : 'Adding episode watches for all episodes in this show.'
))
const showBulkWatchSeasonZeroNoticeVisible = computed(
  () => showBulkWatchSkippedSeasonZeroCount.value > 0,
)
const watchlistButtonText = computed(() => (isShowWatchlisted.value ? 'Watchlisted' : 'Add to watchlist'))
const watchlistButtonShortText = computed(() => (isShowWatchlisted.value ? 'Watchlisted' : 'Watchlist'))
const favouriteButtonText = computed(() => (isShowFavourited.value ? 'Favourited' : 'Add to favourites'))
const favouriteButtonShortText = computed(() => (isShowFavourited.value ? 'Favourited' : 'Favourite'))
const ratingLabelPrefix = computed(() => (isShowRated.value ? 'Rated:' : 'Rate:'))
const ratingLabelValue = computed(() => (isShowRated.value ? `${showRating.value} / 10` : '/ 10'))
const heroBackgroundStyle = computed(() => (
  backgroundUrl.value
    ? { backgroundImage: `url(${backgroundUrl.value})` }
    : undefined
))
const showYearDisplay = computed(() => formatShowYears(show.value))
const desktopSeasonsTopLinkText = computed(() => (
  useAllSeasonsDesktopLink.value ? 'All seasons >' : 'Season details >'
))
const mobileLatestSeasonsAllLinkText = computed(() => `All seasons (${seasonCount.value}) >`)
const titleLengthClass = computed(() => {
  const titleLength = showTitle.value.length
  if (titleLength > 150) return 'item-title--extreme'
  if (titleLength > 120) return 'item-title--very-long'
  if (titleLength > 75) return 'item-title--long'
  return null
})
const mainCastRows = computed(() => {
  const rows = []
  for (let index = 0; index < mainCast.value.length; index += 2) {
    rows.push(mainCast.value.slice(index, index + 2))
  }
  return rows
})

function resetShowPosterLoading(src = show.value.posterUrl) {
  if (!src) {
    showPosterStatus.value = 'loading'
    return
  }
  showPosterStatus.value = src === posterPlaceholderImage ? 'fallback' : 'loading'
}

function handleShowPosterLoad() {
  showPosterStatus.value = show.value.posterUrl === posterPlaceholderImage
    ? 'fallback'
    : 'loaded'
}

function handleShowPosterError() {
  if (show.value.posterUrl !== posterPlaceholderImage) {
    show.value = {
      ...show.value,
      posterUrl: posterPlaceholderImage,
    }
    return
  }
  showPosterStatus.value = 'fallback'
}

function formatShowYears(showEntry) {
  const startYear = showEntry.startYear
  const endYear = showEntry.endYear

  if (!startYear) return ''
  if (!endYear) return `(${startYear} - )`
  if (startYear === endYear) return `(${startYear})`
  return `(${startYear} - ${endYear})`
}

function normalizeSeasons(seasonEntries) {
  if (!Array.isArray(seasonEntries)) return []

  return seasonEntries
    .filter((season) => season?.title)
    .map((season) => ({
      title: season.title,
      episodeCountText: season.episode_count_text || '',
      posterUrl: season.poster_url || '/static/films/img/placeholder-film-camera.svg',
      url: season.url || '#',
      special: Boolean(season.special),
    }))
}

function normalizeNextEpisode(episode) {
  if (!episode || typeof episode !== 'object') return null

  const seasonNumber = episode.season_number ?? null
  const episodeNumber = episode.episode_number ?? null
  const seasonEpisodeText = episode.season_episode_text
    || (seasonNumber !== null && episodeNumber !== null
      ? `Season ${seasonNumber} • Episode ${episodeNumber}`
      : '')

  return {
    id: episode.id ?? null,
    title: episode.title || '',
    seasonNumber,
    episodeNumber,
    seasonEpisodeText,
    posterUrl: episode.poster_url || episode.image_url || episodePlaceholderImage,
    url: episode.url || '#',
  }
}

function normalizeMainCast(castEntries) {
  if (!Array.isArray(castEntries)) return []

  return castEntries
    .slice(0, 6)
    .filter((cast) => cast?.actor)
    .map((cast) => ({
      actor: cast.actor,
      role: cast.role || '',
      image: cast.image || '/static/films/img/placeholder-person.svg',
      url: cast.url || '#',
    }))
}

function normalizeCreditsRegulars(castEntries) {
  if (!Array.isArray(castEntries)) return []

  return castEntries
    .filter((cast) => cast?.name)
    .map((cast) => ({
      name: cast.name,
      role: cast.role || '',
      image: cast.image || '/static/films/img/placeholder-person.svg',
      url: cast.url || '#',
      countText: formatActingAppearanceCount(cast.season_count, cast.episode_count),
    }))
}

function normalizeSeasonEntries(entries) {
  if (!Array.isArray(entries)) return []

  return entries
    .filter((season) => season?.title)
    .map((season) => ({
      number: inferSeasonNumber(season),
      title: season.title,
      overview: season.overview || '',
      episodeCountText: season.episode_count_text || '',
      yearText: season.year_text || '',
      posterUrl: season.poster_url || '/static/films/img/placeholder-film-camera.svg',
      url: season.url || '#',
      stars: Array.isArray(season.stars)
        ? season.stars
          .filter((star) => star?.name)
          .map((star) => ({ name: star.name, url: star.url || '#' }))
        : [],
      special: Boolean(season.special),
    }))
}

function inferSeasonNumber(season) {
  if (Number.isInteger(season?.number)) return season.number
  if (season?.special) return 0

  const match = String(season?.title || '').match(/^Season\s+(\d+)$/i)
  if (!match) return 0

  return Number.parseInt(match[1], 10) || 0
}

function formatActingAppearanceCount(seasonCount, episodeCount) {
  const seasonText = formatAppearanceCountFragment(seasonCount, 'season')
  const episodeText = formatAppearanceCountFragment(episodeCount, 'episode')

  if (seasonText && episodeText) {
    return `on ${seasonText}, and ${episodeText}.`
  }
  if (seasonText) {
    return `on ${seasonText}.`
  }
  if (episodeText) {
    return `on ${episodeText}.`
  }
  return ''
}

function formatAppearanceCountFragment(value, singular) {
  if (!Number.isInteger(value) || value <= 0) return ''
  const suffix = value === 1 ? singular : `${singular}s`
  return `${value} ${suffix}`
}

function filterShowCastMembers(members) {
  if (!showCastFilterTerm.value) return members

  return members.filter((member) => {
    const name = String(member.name || '').toLowerCase()
    const role = String(member.role || '').toLowerCase()
    return name.includes(showCastFilterTerm.value) || role.includes(showCastFilterTerm.value)
  })
}

function syncShowOverviewLinksViewport() {
  if (typeof window === 'undefined') return
  useAllSeasonsDesktopLink.value = window.innerWidth > 600
}

function setSeasonSortMode(mode) {
  if (mode === 'oldest' || mode === 'newest') {
    seasonSortMode.value = mode
  }
}

function setSeasonListingMode(mode) {
  if (mode === '1' || mode === '2' || mode === '3') {
    seasonListingMode.value = mode
  }
}

function normalizeSeasonWatchProgress(progressEntries) {
  const progress = {}
  if (!Array.isArray(progressEntries)) return progress

  progressEntries.forEach((entry) => {
    const number = Number.parseInt(entry?.number, 10)
    const totalCount = Number.parseInt(entry?.total_count, 10)
    if (!Number.isInteger(number) || !Number.isInteger(totalCount) || totalCount <= 0) {
      return
    }

    const watchedCount = Number.parseInt(entry?.count, 10)
    progress[number] = {
      count: Number.isInteger(watchedCount) && watchedCount > 0 ? watchedCount : 0,
      totalCount,
    }
  })

  return progress
}

function seasonWatchProgress(season) {
  const number = Number.parseInt(season?.number, 10)
  if (!Number.isInteger(number)) return null
  return showSeasonWatchProgress.value[number] || null
}

function seasonWatchProgressVisible(season) {
  return isAuthenticated.value
    && showWatchStatusLoaded.value
    && !season?.special
    && Boolean(seasonWatchProgress(season))
}

function seasonWatchProgressCountText(season) {
  const progress = seasonWatchProgress(season)
  if (!progress) return ''
  return `${progress.count}/${progress.totalCount}`
}

function seasonWatchProgressCompactText(season) {
  return seasonWatchProgressCountText(season)
}

function seasonWatchProgressPercentage(season) {
  const progress = seasonWatchProgress(season)
  if (!progress || progress.totalCount <= 0) return 0
  return Math.min(100, Math.ceil((progress.count / progress.totalCount) * 100))
}

function seasonWatchProgressComplete(season) {
  const progress = seasonWatchProgress(season)
  return Boolean(progress && progress.count >= progress.totalCount)
}

function seasonOverviewKey(season) {
  if (!season || typeof season !== 'object') {
    return ''
  }
  if (season.number !== null && season.number !== undefined && season.number !== '') {
    return `number:${season.number}`
  }
  return `title:${season.title || ''}`
}

function hasExpandableSeasonOverview(season) {
  return hasExpandableOverview(season?.overview, seasonOverviewClipLimit)
}

function seasonOverviewExpanded(season) {
  const key = seasonOverviewKey(season)
  return Boolean(key && expandedSeasonOverviews.value[key])
}

function displayedSeasonOverview(season) {
  const overview = season?.overview || ''
  if (!hasExpandableSeasonOverview(season) || seasonOverviewExpanded(season)) {
    return overview
  }
  return clipOverviewWithSuffix(overview, seasonOverviewClipLimit, '')
}

function toggleSeasonOverview(season) {
  const key = seasonOverviewKey(season)
  if (!key) return
  expandedSeasonOverviews.value = {
    ...expandedSeasonOverviews.value,
    [key]: !expandedSeasonOverviews.value[key],
  }
}

function buildAuthHeaders() {
  return auth.state.access
    ? { Authorization: `Bearer ${auth.state.access}` }
    : {}
}

function normalizeOverviewListItem(item) {
  if (!item || typeof item !== 'object') {
    return null
  }

  const posterUrl = String(item.poster_url || '').trim()
  const title = String(item.title || '').trim()
  const year = String(item.year || item.start_year || '').trim()
  if (!posterUrl || !title) {
    return null
  }
  const placeholderLabel = year ? `${title} (${year})` : title
  const showPlaceholderLabel = posterUrl.includes('placeholder-film-camera.svg')

  return {
    id: item.id ?? `${title}-${posterUrl}`,
    title,
    year,
    posterUrl,
    placeholderLabel,
    showPlaceholderLabel,
  }
}

function slugifyText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function buildShowListPath(listEntry) {
  if (listEntry?.is_curated && listEntry.url) return listEntry.url
  const listId = Number.parseInt(String(listEntry?.id || ''), 10)
  if (!listId) {
    return '#'
  }

  const titleSlug = slugifyText(listEntry?.title || '') || 'list'
  return `/lists/show/${listId}-${titleSlug}`
}

function buildFilmsomniacPath(username) {
  const normalizedUsername = String(username || '').trim()
  return normalizedUsername ? `/filmsomniacs/${encodeURIComponent(normalizedUsername)}` : '#'
}

function normalizeShowList(listEntry, itemLimit = 4) {
  if (!listEntry || typeof listEntry !== 'object') {
    return null
  }

  const title = String(listEntry.title || '').trim()
  if (!title) {
    return null
  }

  const items = Array.isArray(listEntry.items)
    ? listEntry.items.map(normalizeOverviewListItem).filter(Boolean).slice(0, itemLimit)
    : []

  return {
    id: listEntry.id ?? title,
    title,
    href: buildShowListPath(listEntry),
    official: Boolean(listEntry.official),
    is_curated: Boolean(listEntry.is_curated),
    ownerName: String(listEntry.owner_name || '').trim(),
    ownerHref: buildFilmsomniacPath(listEntry.owner_name),
    items,
  }
}

function normalizeOverviewList(listEntry) {
  return normalizeShowList(listEntry, 4)
}

function normalizeShowListsPageEntry(listEntry) {
  return normalizeShowList(listEntry, 8)
}

function normalizeRatingValue(value) {
  const parsed = Number.parseInt(value, 10)
  return Number.isInteger(parsed) && parsed >= 1 && parsed <= 10 ? parsed : null
}

function formatRelativeTime(value, fallback = '') {
  if (!value) return fallback

  const targetTime = Date.parse(value)
  if (Number.isNaN(targetTime)) return fallback

  const diffMs = targetTime - Date.now()
  const absSeconds = Math.abs(diffMs) / 1000
  const absMinutes = absSeconds / 60
  const absHours = absMinutes / 60
  const absDays = absHours / 24
  const absMonths = absDays / 30
  const rtf = typeof Intl !== 'undefined' && typeof Intl.RelativeTimeFormat === 'function'
    ? new Intl.RelativeTimeFormat('en', { numeric: 'auto' })
    : null

  if (absSeconds < 60) return rtf ? rtf.format(Math.round(diffMs / 1000), 'second') : fallback
  if (absMinutes < 60) return rtf ? rtf.format(Math.round(diffMs / 60000), 'minute') : fallback
  if (absHours < 24) return rtf ? rtf.format(Math.round(diffMs / 3600000), 'hour') : fallback
  if (absDays < 30) return rtf ? rtf.format(Math.round(diffMs / 86400000), 'day') : fallback
  if (absMonths < 12) return rtf ? rtf.format(Math.round(diffMs / 2592000000), 'month') : fallback
  return rtf ? rtf.format(Math.round(diffMs / 31536000000), 'year') : fallback
}

function normalizeOverviewComment(comment) {
  return {
    id: comment.id,
    parentId: comment.parent_id ?? null,
    level: Number(comment.level ?? 0),
    comment: String(comment.comment || ''),
    containsSpoilers: Boolean(comment.contains_spoilers),
    submitDate: comment.submit_date || null,
    submitDateText: comment.submit_date_text || '',
    username: String(comment.username || ''),
    userHref: buildFilmsomniacPath(comment.username),
  }
}

const OVERVIEW_COMMENT_PREVIEW_LIMIT = 500

function clipOverviewCommentPreview(commentText) {
  const previewText = String(commentText || '')
  if (previewText.length <= OVERVIEW_COMMENT_PREVIEW_LIMIT) {
    return previewText
  }

  const clippedText = previewText.slice(0, OVERVIEW_COMMENT_PREVIEW_LIMIT).trimEnd()
  const lastSpaceIndex = clippedText.lastIndexOf(' ')
  const clippedToWord = lastSpaceIndex > 0 ? clippedText.slice(0, lastSpaceIndex) : clippedText
  return `${clippedToWord} ...`
}

function overviewCommentPreviewText(comment) {
  if (comment.containsSpoilers) {
    return 'This comment contains spoilers.'
  }
  return clipOverviewCommentPreview(comment.comment)
}

function overviewCommentIsTruncated(comment) {
  return !comment.containsSpoilers && comment.comment.length > OVERVIEW_COMMENT_PREVIEW_LIMIT
}

function prependOverviewList(listEntry) {
  const normalized = normalizeOverviewList(listEntry)
  if (!normalized) {
    return
  }

  const existing = Array.isArray(show.value.overviewLists) ? show.value.overviewLists : []
  const nextOverviewLists = [
    normalized,
    ...existing.filter((entry) => String(entry.id) !== String(normalized.id)),
  ].slice(0, 2)

  show.value = {
    ...show.value,
    overviewLists: nextOverviewLists,
  }
}

function prependShowListsPageEntry(listEntry) {
  const normalized = normalizeShowListsPageEntry(listEntry)
  if (!normalized) {
    return
  }

  const existing = Array.isArray(showListsPageEntries.value) ? showListsPageEntries.value : []
  const alreadyExists = existing.some((entry) => String(entry.id) === String(normalized.id))
  showListsPageNumber.value = 1
  showListsPageEntries.value = [
    normalized,
    ...existing.filter((entry) => String(entry.id) !== String(normalized.id)),
  ].slice(0, showListsPageSize.value)
  showListsPageCount.value = alreadyExists ? showListsPageCount.value : showListsPageCount.value + 1
  showListsNumPages.value = Math.max(1, Math.ceil(showListsPageCount.value / showListsPageSize.value))
  showListsPageLoaded.value = true
  showListsPageError.value = ''
}

function overviewCommentTimeText(comment) {
  return formatRelativeTime(comment.submitDate, comment.submitDateText)
}

function compareOverviewComments(a, b) {
  const aTime = Date.parse(a.submitDate || '') || 0
  const bTime = Date.parse(b.submitDate || '') || 0
  return bTime - aTime
}

function closeShowBulkWatchModal() {
  if (showBulkWatchPending.value) {
    return
  }
  showBulkWatchModalOpen.value = false
  showBulkWatchCompleted.value = false
  showBulkWatchSkippedSeasonZeroCount.value = 0
}

function resetShowListModalForm() {
  showListModalError.value = ''
  showListCreateMode.value = false
  showListSelectedListId.value = ''
  showListPickerOpen.value = false
  showListCreateTitle.value = ''
  showListCreateDescription.value = ''
  showListCreatePublic.value = true
}

let showListSuccessDismissTimer = null

function clearShowListSuccessNotice(force = false) {
  if (showListSuccessDismissTimer) {
    clearTimeout(showListSuccessDismissTimer)
    showListSuccessDismissTimer = null
  }
  showListSuccessDismissing.value = false
  if (force) {
    showListSuccessTitle.value = ''
  }
}

function showShowListSuccessNotice(title) {
  clearShowListSuccessNotice(true)
  showListSuccessTitle.value = String(title || '').trim()
}

function dismissShowListSuccessNotice() {
  if (!showListSuccessTitle.value || showListSuccessDismissing.value) {
    return
  }
  showListSuccessDismissing.value = true
  showListSuccessDismissTimer = setTimeout(() => {
    showListSuccessTitle.value = ''
    showListSuccessDismissing.value = false
    showListSuccessDismissTimer = null
  }, 240)
}

function closeShowListModal(force = false) {
  if (showListModalPending.value && !force) {
    return
  }
  showListModalOpen.value = false
  showListModalLoading.value = false
  resetShowListModalForm()
}

function openShowListCreateMode() {
  showListModalError.value = ''
  showListPickerOpen.value = false
  showListCreateMode.value = true
}

function selectShowList(listId) {
  showListSelectedListId.value = String(listId || '')
  showListPickerOpen.value = false
}

async function loadOwnedShowLists(currentSlug) {
  showListOptions.value = []
  showListSelectedListId.value = ''
  showListPickerOpen.value = false
  showListModalLoading.value = true
  showListModalError.value = ''

  try {
    const response = await fetch(`/api/v1/shows/${currentSlug}/lists`, {
      headers: buildAuthHeaders(),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      closeShowListModal(true)
      return
    }
    if (!response.ok) {
      showListModalError.value = 'Unable to load your lists right now.'
      return
    }
    const data = await response.json()
    showListOptions.value = Array.isArray(data.results) ? data.results : []
  } catch {
    showListModalError.value = 'Unable to load your lists right now.'
  } finally {
    showListModalLoading.value = false
  }
}

async function openShowListModal() {
  if (!slug.value || !isAuthenticated.value || showListModalPending.value) {
    return
  }

  showListModalOpen.value = true
  resetShowListModalForm()
  await loadOwnedShowLists(slug.value)
}

async function loadShowListsPage(
  pageNumber = showListsPageNumber.value,
  currentSlug = slug.value,
  section = activeSection.value,
  includeAuth = isAuthenticated.value,
) {
  showListsPageEntries.value = []
  showListsPageError.value = ''
  showListsPageLoading.value = true
  showListsPageLoaded.value = false

  if (!currentSlug || section !== 'lists' || typeof fetch !== 'function') {
    showListsPageLoading.value = false
    return
  }

  const normalizedPageNumber = Math.max(1, Number.parseInt(pageNumber, 10) || 1)
  const headers = includeAuth ? buildAuthHeaders() : {}
  const requestOptions = Object.keys(headers).length > 0 ? { headers } : undefined

  try {
    const response = await fetch(
      `/api/v1/shows/${currentSlug}/lists/page?page=${normalizedPageNumber}`,
      requestOptions,
    )
    if (response.status === 401 || response.status === 403) {
      if (includeAuth) {
        auth.clearTokens()
        await loadShowListsPage(normalizedPageNumber, currentSlug, section, false)
      }
      return
    }
    if (!response.ok) {
      showListsPageError.value = 'Unable to load lists right now.'
      return
    }
    const data = await response.json()
    showListsPageNumber.value = Number.parseInt(data.page, 10) || normalizedPageNumber
    showListsPageSize.value = Number.parseInt(data.page_size, 10) || 10
    showListsPageCount.value = Number.parseInt(data.count, 10) || 0
    showListsNumPages.value = Math.max(1, Number.parseInt(data.num_pages, 10) || 1)
    showListsPageEntries.value = Array.isArray(data.results)
      ? data.results.map(normalizeShowListsPageEntry).filter(Boolean)
      : []
    showListsPageLoaded.value = true
  } catch {
    showListsPageError.value = 'Unable to load lists right now.'
  } finally {
    showListsPageLoading.value = false
  }
}

function goToShowListsPage(pageNumber) {
  const normalizedPageNumber = Number.parseInt(pageNumber, 10) || 1
  if (
    showListsPageLoading.value ||
    normalizedPageNumber < 1 ||
    normalizedPageNumber > showListsNumPages.value
  ) {
    return
  }
  loadShowListsPage(normalizedPageNumber)
}

async function submitShowListAddition() {
  if (
    showListModalPending.value ||
    !slug.value ||
    !isAuthenticated.value ||
    !showListSelectedListId.value ||
    typeof fetch !== 'function'
  ) {
    return
  }

  showListModalPending.value = true
  showListModalError.value = ''
  try {
    const response = await fetch(`/api/v1/shows/${slug.value}/lists`, {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ list_id: Number.parseInt(showListSelectedListId.value, 10) }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      closeShowListModal(true)
      return
    }
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      showListModalError.value = data.list_id || 'Unable to add this show to the list right now.'
      return
    }
    showShowListSuccessNotice(data.list?.title)
    prependOverviewList(data.overview_list)
    prependShowListsPageEntry(data.overview_list)
    closeShowListModal(true)
  } catch {
    showListModalError.value = 'Unable to add this show to the list right now.'
  } finally {
    showListModalPending.value = false
  }
}

async function submitShowListCreate() {
  if (
    showListModalPending.value ||
    !slug.value ||
    !isAuthenticated.value ||
    typeof fetch !== 'function'
  ) {
    return
  }

  showListModalPending.value = true
  showListModalError.value = ''
  try {
    const response = await fetch(`/api/v1/shows/${slug.value}/lists/create`, {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        title: showListCreateTitle.value,
        description: showListCreateDescription.value,
        public: showListCreatePublic.value,
      }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      closeShowListModal(true)
      return
    }
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      showListModalError.value = data.title || 'Unable to create the list right now.'
      return
    }
    showShowListSuccessNotice(data.list?.title)
    prependOverviewList(data.overview_list)
    prependShowListsPageEntry(data.overview_list)
    closeShowListModal(true)
  } catch {
    showListModalError.value = 'Unable to create the list right now.'
  } finally {
    showListModalPending.value = false
  }
}

async function loadShowDetail(currentSlug) {
  const confirmPageView = capturePageView()
  const requestedSection = activeSection.value
  showDetailRequestPending.value = true
  embeddedViewerStateSlug.value = ''
  showBulkWatchPending.value = false
  showBulkWatchModalOpen.value = false
  showBulkWatchCompleted.value = false
  showBulkWatchSkippedSeasonZeroCount.value = 0
  show.value = createFallbackShowState(currentSlug)
  seasonFilter.value = ''
  seasonSortMode.value = 'newest'
  seasonListingMode.value = '1'
  showCastFilter.value = ''
  showOverviewExpanded.value = false
  showLoaded.value = false
  showNotFound.value = false
  showCreditsLoadedSlug.value = ''
  showOverviewLoaded.value = false
  showSeasonsLoaded.value = false
  if (!currentSlug || typeof fetch !== 'function') {
    showLoaded.value = true
    showDetailRequestPending.value = false
    return
  }

  try {
    const authHeaders = buildAuthHeaders()
    const detailUrl = ['seasons', 'credits', 'lists', 'comments'].includes(requestedSection)
      ? `/api/v1/shows/${currentSlug}/?section=${encodeURIComponent(requestedSection)}`
      : `/api/v1/shows/${currentSlug}/`
    const response = Object.keys(authHeaders).length
      ? await fetch(detailUrl, { headers: authHeaders })
      : await fetch(detailUrl)
    if (response.status === 404) {
      showNotFound.value = true
      return
    }
    if (!response.ok) return
    const data = await response.json()
    confirmPageView()
    if (currentSlug !== slug.value || requestedSection !== activeSection.value) {
      if (currentSlug === slug.value && activeSection.value === 'overview' && !showOverviewLoaded.value) {
        loadShowDetail(slug.value)
      }
      if (currentSlug === slug.value && activeSection.value === 'seasons' && !showSeasonsLoaded.value) {
        loadShowDetail(slug.value)
      }
      return
    }
    const hasApiField = (field) => Object.prototype.hasOwnProperty.call(data, field)
    show.value = {
      ...show.value,
      id: hasApiField('id') ? data.id : show.value.id,
      title: data.title || show.value.title,
      overview: hasApiField('overview') ? data.overview || '' : show.value.overview,
      posterUrl: hasApiField('poster_url') ? data.poster_url || posterPlaceholderImage : show.value.posterUrl,
      backgroundUrl: hasApiField('background_url') ? data.background_url || defaultBackgroundImage : show.value.backgroundUrl,
      mobileBackgroundUrl: hasApiField('mobile_background_url') ? data.mobile_background_url || '' : show.value.mobileBackgroundUrl,
      mainCast: hasApiField('main_cast') ? normalizeMainCast(data.main_cast) : show.value.mainCast,
      creditsRegulars: hasApiField('credits_regulars')
        ? normalizeCreditsRegulars(data.credits_regulars)
        : show.value.creditsRegulars,
      seasonCount: hasApiField('season_count') ? data.season_count || 0 : show.value.seasonCount,
      startYear: hasApiField('start_year') ? data.start_year : show.value.startYear,
      endYear: hasApiField('end_year') ? data.end_year : show.value.endYear,
      canAccessBadmin: hasApiField('can_access_badmin')
        ? data.can_access_badmin === true
        : show.value.canAccessBadmin,
      seasonsTitle: hasApiField('seasons_title') ? data.seasons_title : show.value.seasonsTitle,
      allSeasonsTitle: hasApiField('all_seasons_title') ? data.all_seasons_title : show.value.allSeasonsTitle,
      totalEpisodeCount: hasApiField('total_episode_count')
        ? data.total_episode_count || 0
        : show.value.totalEpisodeCount,
      totalEpisodeCountText: hasApiField('total_episode_count_text')
        ? data.total_episode_count_text
        : show.value.totalEpisodeCountText,
      overviewSeasons: hasApiField('overview_seasons')
        ? normalizeSeasons(data.overview_seasons)
        : show.value.overviewSeasons,
      latestSeasons: hasApiField('latest_seasons')
        ? normalizeSeasons(data.latest_seasons)
        : show.value.latestSeasons,
      seasonEntries: hasApiField('season_entries')
        ? normalizeSeasonEntries(data.season_entries)
        : show.value.seasonEntries,
      overviewLists: hasApiField('overview_lists')
        ? (Array.isArray(data.overview_lists)
            ? data.overview_lists.map(normalizeOverviewList).filter(Boolean)
            : [])
        : show.value.overviewLists,
    }
    if (hasApiField('viewer_state')) {
      applyShowViewerState(data.viewer_state)
      embeddedViewerStateSlug.value = currentSlug
    }
    if (requestedSection === 'credits' && hasApiField('credits_regulars')) {
      showCreditsLoadedSlug.value = currentSlug
    }
    if (requestedSection === 'overview') {
      showOverviewLoaded.value = true
    }
    if (hasApiField('season_entries')) {
      showSeasonsLoaded.value = true
    }
  } catch {
    // Keep the empty fallback state until the real API data is available.
  } finally {
    showLoaded.value = true
    showDetailRequestPending.value = false
    if (
      currentSlug &&
      isAuthenticated.value &&
      embeddedViewerStateSlug.value !== currentSlug
    ) {
      loadShowViewerState(currentSlug)
    }
  }
}

async function loadShowCredits(currentSlug) {
  if (
    !currentSlug ||
    typeof fetch !== 'function' ||
    showCreditsLoadedSlug.value === currentSlug
  ) {
    return
  }

  try {
    const authHeaders = buildAuthHeaders()
    const detailUrl = `/api/v1/shows/${currentSlug}/?section=credits`
    const response = Object.keys(authHeaders).length
      ? await fetch(detailUrl, { headers: authHeaders })
      : await fetch(detailUrl)
    if (!response.ok) return
    const data = await response.json()
    if (Object.prototype.hasOwnProperty.call(data, 'credits_regulars')) {
      show.value = {
        ...show.value,
        creditsRegulars: normalizeCreditsRegulars(data.credits_regulars),
      }
      showCreditsLoadedSlug.value = currentSlug
    }
  } catch {
    // Keep the overview cast data until the full credits payload is available.
  }
}

async function loadShowNextEpisode(currentSlug) {
  show.value = {
    ...show.value,
    nextEpisode: null,
  }

  if (!currentSlug || !auth.state.access || typeof fetch !== 'function') {
    return
  }

  try {
    const response = await fetch(`/api/v1/shows/${currentSlug}/next-episode`, {
      headers: buildAuthHeaders(),
    })
    if (!response.ok) return
    const data = await response.json()
    show.value = {
      ...show.value,
      nextEpisode: normalizeNextEpisode(data.next_episode),
    }
  } catch {
    show.value = {
      ...show.value,
      nextEpisode: null,
    }
  }
}

async function loadShowViewerState(currentSlug) {
  showWatchlisted.value = false
  showFavourited.value = false
  showRating.value = null
  showWatchedEpisodeCount.value = 0
  showEpisodeCount.value = 0
  showBulkWatchSkippedSeasonZeroCount.value = 0
  showWatchStatusLoaded.value = false
  showSeasonWatchProgress.value = {}
  show.value = {
    ...show.value,
    nextEpisode: null,
  }

  if (!currentSlug || !isAuthenticated.value || typeof fetch !== 'function') {
    return
  }

  try {
    const response = await fetch(`/api/v1/shows/${currentSlug}/viewer-state`, {
      headers: buildAuthHeaders(),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      showBulkWatchModalOpen.value = false
      return
    }
    if (!response.ok) {
      showBulkWatchModalOpen.value = false
      await loadLegacyShowViewerState(currentSlug)
      return
    }

    applyShowViewerState(await response.json())
  } catch {
    await loadLegacyShowViewerState(currentSlug)
  }
}

function applyShowViewerState(data) {
  const hasViewerField = (field) => Object.prototype.hasOwnProperty.call(data || {}, field)
  const watch = data && typeof data.watch === 'object' && data.watch !== null ? data.watch : {}
  showWatchlisted.value = Boolean(data?.watchlisted)
  showFavourited.value = Boolean(data?.favourited)
  showRating.value = normalizeRatingValue(data?.rating)

  if (hasViewerField('watch')) {
    showWatchedEpisodeCount.value = Number.isInteger(watch.count) ? watch.count : 0
    showEpisodeCount.value = Number.isInteger(watch.total_count) ? watch.total_count : 0
    showBulkWatchSkippedSeasonZeroCount.value = Number.isInteger(watch.skipped_season_zero_count)
      ? watch.skipped_season_zero_count
      : 0
    showSeasonWatchProgress.value = normalizeSeasonWatchProgress(watch.season_progress)
    showWatchStatusLoaded.value = true
  }

  if (hasViewerField('next_episode')) {
    show.value = {
      ...show.value,
      nextEpisode: normalizeNextEpisode(data?.next_episode),
    }
  }
}

async function loadLegacyShowViewerState(currentSlug) {
  await Promise.all([
    loadShowNextEpisode(currentSlug),
    loadShowWatchStatus(currentSlug),
    loadShowWatchlistStatus(currentSlug),
    loadShowFavouriteStatus(currentSlug),
    loadShowRatingStatus(currentSlug),
  ])
}

async function loadLatestOverviewComments(showId, section = activeSection.value) {
  latestOverviewComments.value = []
  latestOverviewCommentsLoaded.value = false

  if (!showId || section !== 'overview' || typeof fetch !== 'function') {
    return
  }

  try {
    const response = await fetch(`/api/v1/comments/show/${showId}/`)
    if (!response.ok) {
      return
    }
    const data = await response.json()
    const results = Array.isArray(data.results) ? data.results.map(normalizeOverviewComment) : []
    latestOverviewComments.value = results
      .filter((comment) => comment.parentId === null && comment.level === 0)
      .sort(compareOverviewComments)
      .slice(0, 2)
    latestOverviewCommentsLoaded.value = true
  } catch {
    // Keep the section quiet if the preview comments request fails.
  }
}

async function loadShowWatchlistStatus(currentSlug) {
  showWatchlisted.value = false

  if (!currentSlug || !isAuthenticated.value || typeof fetch !== 'function') {
    return
  }

  try {
    const response = await fetch(`/api/v1/shows/${currentSlug}/watchlist`, {
      headers: buildAuthHeaders(),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      showBulkWatchModalOpen.value = false
      return
    }
    if (!response.ok) {
      showBulkWatchModalOpen.value = false
      return
    }
    const data = await response.json()
    showWatchlisted.value = Boolean(data.watchlisted)
  } catch {
    // Keep the control in the default state if the watchlist status request fails.
  }
}

async function loadShowWatchStatus(currentSlug) {
  showWatchedEpisodeCount.value = 0
  showEpisodeCount.value = 0
  showBulkWatchSkippedSeasonZeroCount.value = 0
  showWatchStatusLoaded.value = false
  showSeasonWatchProgress.value = {}

  if (!currentSlug || !isAuthenticated.value || typeof fetch !== 'function') {
    return
  }

  try {
    const response = await fetch(`/api/v1/shows/${currentSlug}/watch`, {
      headers: buildAuthHeaders(),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) return
    const data = await response.json()
    showWatchedEpisodeCount.value = Number.isInteger(data.count) ? data.count : 0
    showEpisodeCount.value = Number.isInteger(data.total_count) ? data.total_count : 0
    showBulkWatchSkippedSeasonZeroCount.value = Number.isInteger(data.skipped_season_zero_count)
      ? data.skipped_season_zero_count
      : 0
    showSeasonWatchProgress.value = normalizeSeasonWatchProgress(data.season_progress)
    showWatchStatusLoaded.value = true
  } catch {
    // Keep the control hidden if the show watch status request fails.
  }
}

async function loadShowFavouriteStatus(currentSlug) {
  showFavourited.value = false

  if (!currentSlug || !isAuthenticated.value || typeof fetch !== 'function') {
    return
  }

  try {
    const response = await fetch(`/api/v1/shows/${currentSlug}/favourite`, {
      headers: buildAuthHeaders(),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) return
    const data = await response.json()
    showFavourited.value = Boolean(data.favourited)
  } catch {
    // Keep the control in the default state if the favourite status request fails.
  }
}

async function loadShowRatingStatus(currentSlug) {
  showRating.value = null

  if (!currentSlug || !isAuthenticated.value || typeof fetch !== 'function') {
    return
  }

  try {
    const response = await fetch(`/api/v1/shows/${currentSlug}/rating`, {
      headers: buildAuthHeaders(),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) return
    const data = await response.json()
    showRating.value = normalizeRatingValue(data.rating)
  } catch {
    // Keep the control in the default state if the rating status request fails.
  }
}

async function toggleShowWatchlist() {
  if (showWatchlistPending.value || !slug.value || !isAuthenticated.value || typeof fetch !== 'function') {
    return
  }

  showWatchlistPending.value = true
  try {
    const response = await fetch(`/api/v1/shows/${slug.value}/watchlist`, {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ source: 'S' }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) return
    const data = await response.json()
    showWatchlisted.value = Boolean(data.watchlisted)
  } catch {
    // Leave the current button state unchanged on network or API failure.
  } finally {
    showWatchlistPending.value = false
  }
}

async function toggleShowFavourite() {
  if (showFavouritePending.value || !slug.value || !isAuthenticated.value || typeof fetch !== 'function') {
    return
  }

  showFavouritePending.value = true
  try {
    const response = await fetch(`/api/v1/shows/${slug.value}/favourite`, {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ source: 'S' }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) return
    const data = await response.json()
    showFavourited.value = Boolean(data.favourited)
  } catch {
    // Leave the current button state unchanged on network or API failure.
  } finally {
    showFavouritePending.value = false
  }
}

async function markShowAsWatched() {
  if (
    showBulkWatchPending.value ||
    !slug.value ||
    !isAuthenticated.value ||
    typeof fetch !== 'function'
  ) {
    return
  }

  const shouldMarkShowAsWatched = window.confirm(
    `Are sure you want to add every episode (${showEpisodeCount.value}) of "${showTitle.value}", excluding specials, as watched?`,
  )
  if (!shouldMarkShowAsWatched) {
    return
  }

  showBulkWatchPending.value = true
  showBulkWatchModalOpen.value = true
  showBulkWatchCompleted.value = false
  showBulkWatchSkippedSeasonZeroCount.value = 0
  try {
    const response = await fetch(`/api/v1/shows/${slug.value}/watch`, {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ source: 'L' }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) return
    const data = await response.json()
    showWatchedEpisodeCount.value = Number.isInteger(data.count) ? data.count : showWatchedEpisodeCount.value
    showEpisodeCount.value = Number.isInteger(data.total_count) ? data.total_count : showEpisodeCount.value
    showBulkWatchSkippedSeasonZeroCount.value = Number.isInteger(data.skipped_season_zero_count)
      ? data.skipped_season_zero_count
      : 0
    showSeasonWatchProgress.value = normalizeSeasonWatchProgress(data.season_progress)
    showWatchStatusLoaded.value = true
    showBulkWatchCompleted.value = true
  } catch {
    // Leave the show watch state unchanged on network or API failure.
    showBulkWatchModalOpen.value = false
  } finally {
    showBulkWatchPending.value = false
  }
}

async function setShowRating(ratingValue) {
  if (
    showRatingPending.value ||
    !slug.value ||
    !isAuthenticated.value ||
    typeof fetch !== 'function'
  ) {
    return
  }

  showRatingPending.value = true
  try {
    const response = await fetch(`/api/v1/shows/${slug.value}/rating`, {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ rating: ratingValue, source: 'S' }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) return
    const data = await response.json()
    showRating.value = normalizeRatingValue(data.rating)
    hoverRating.value = 0
  } catch {
    // Leave the current rating unchanged on network or API failure.
  } finally {
    showRatingPending.value = false
  }
}

async function removeShowRating() {
  if (
    showRatingPending.value ||
    !slug.value ||
    !isAuthenticated.value ||
    !isShowRated.value ||
    typeof fetch !== 'function'
  ) {
    return
  }

  showRatingPending.value = true
  try {
    const response = await fetch(`/api/v1/shows/${slug.value}/rating`, {
      method: 'DELETE',
      headers: buildAuthHeaders(),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) return
    showRating.value = null
    hoverRating.value = 0
  } catch {
    // Leave the current rating unchanged on network or API failure.
  } finally {
    showRatingPending.value = false
  }
}

watch(slug, loadShowDetail, { immediate: true })
watch(documentTitle, setDocumentTitle, { immediate: true })
watch(() => show.value.posterUrl, (src) => {
  resetShowPosterLoading(src)
}, { immediate: true })
watch(slug, () => {
  showListOptions.value = []
  clearShowListSuccessNotice(true)
  closeShowListModal(true)
  showListsPageLoading.value = false
  showListsPageLoaded.value = false
  showListsPageError.value = ''
  showListsPageNumber.value = 1
  showListsPageSize.value = 10
  showListsPageCount.value = 0
  showListsNumPages.value = 1
  showListsPageEntries.value = []
  showOverviewLoaded.value = false
  showSeasonsLoaded.value = false
  showCreditsLoadedSlug.value = ''
})
watch([() => show.value.id, activeSection], ([showId, section]) => {
  loadLatestOverviewComments(showId, section)
}, { immediate: true })
watch(activeSection, (section) => {
  if (section === 'overview' && showLoaded.value && !showOverviewLoaded.value) {
    loadShowDetail(slug.value)
  }
  if (section === 'seasons' && showLoaded.value && !showSeasonsLoaded.value) {
    loadShowDetail(slug.value)
  }
  if (section === 'credits' && !showDetailRequestPending.value) {
    loadShowCredits(slug.value)
  }
  if (section === 'lists') {
    loadShowListsPage(showListsPageNumber.value)
  }
}, { immediate: true })
watch([slug, isAuthenticated], ([currentSlug, authenticated]) => {
  if (!authenticated) {
    showListModalPending.value = false
    showListModalLoading.value = false
    showListOptions.value = []
    clearShowListSuccessNotice(true)
    closeShowListModal(true)
    showBulkWatchPending.value = false
    showBulkWatchModalOpen.value = false
    showBulkWatchCompleted.value = false
    showBulkWatchSkippedSeasonZeroCount.value = 0
    showWatchedEpisodeCount.value = 0
    showEpisodeCount.value = 0
    showWatchStatusLoaded.value = false
    showSeasonWatchProgress.value = {}
    showWatchlisted.value = false
    showWatchlistPending.value = false
    showFavourited.value = false
    showFavouritePending.value = false
    showRating.value = null
    showRatingPending.value = false
    show.value = {
      ...show.value,
      nextEpisode: null,
    }
    if (activeSection.value === 'lists' && showListsPageLoaded.value) {
      loadShowListsPage(showListsPageNumber.value, currentSlug, 'lists', false)
    }
    return
  }
  if (
    showDetailRequestPending.value ||
    embeddedViewerStateSlug.value === currentSlug
  ) {
    if (activeSection.value === 'lists' && showListsPageLoaded.value) {
      loadShowListsPage(showListsPageNumber.value, currentSlug, 'lists', true)
    }
    return
  }
  loadShowViewerState(currentSlug)
  if (activeSection.value === 'lists' && showListsPageLoaded.value) {
    loadShowListsPage(showListsPageNumber.value, currentSlug, 'lists', true)
  }
}, { immediate: true })

onMounted(() => {
  syncShowOverviewLinksViewport()
  window.addEventListener('resize', syncShowOverviewLinksViewport)
})

onBeforeUnmount(() => {
  clearShowListSuccessNotice(true)
  if (typeof window === 'undefined') return
  window.removeEventListener('resize', syncShowOverviewLinksViewport)
})

function previewRating(star) {
  hoverRating.value = star
}

function resetRatingPreview() {
  hoverRating.value = 0
}

function ratingStarClass(star) {
  const visibleRating = hoverRating.value || showRating.value || 0
  return [
    'rating-star',
    star <= visibleRating ? 'rating-star--preview ion-ios-star' : 'ion-ios-star-outline',
  ]
}

function scrollShowCastToAnchor(event) {
  const href = event?.currentTarget?.getAttribute('href')
  if (!href) return
  const anchorId = href.replace(/^#/, '')
  if (!anchorId) return

  const anchor = document.getElementById(anchorId)
  if (!anchor) return
  anchor.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function toggleShowOverview() {
  if (!hasExpandableShowOverview.value) return
  showOverviewExpanded.value = !showOverviewExpanded.value
}

useMetaDescription(() => buildMetaDescription(
  'show',
  hasLoadedShowPayload.value ? { title: showTitle.value, years: formatShowYears(show.value) } : {},
  activeSection.value,
))
</script>
